/**
 * useVoiceScoring — hands-free ball scoring via Web Speech API.
 *
 * Recognises a small grammar of cricket commands and dispatches them to
 * the existing ball state + save/undo handlers. All processing is local
 * to the browser; nothing is sent to a server.
 *
 *   "dot"            → 0 runs
 *   "single", "one"  → 1 run
 *   "two", "couple"  → 2
 *   "three"          → 3
 *   "four", "boundary" → 4 (boundary)
 *   "six", "maximum"   → 6 (boundary)
 *   "wide"           → toggle is_wide
 *   "no ball"        → toggle is_no_ball
 *   "bye"            → toggle is_bye
 *   "leg bye"        → toggle is_leg_bye
 *   "dead ball"      → toggle is_dead_ball
 *   "wicket", "out"  → has_wicket=true (no type — picker prompts)
 *   "bowled" / "caught" / "lbw" / "run out" / "stumped" / "hit wicket"
 *                    → has_wicket + wicket_type
 *   "save"           → trigger save (uses 2-second hold)
 *   "undo"           → trigger undo
 *   "cancel"         → cancel pending save
 *
 * Compounds work: "four boundary", "wide one", "caught off bumrah".
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { wagonCellNumberToSliceBand } from "../field-engine/regionGenerator";
import { resolvePosition } from "../field-engine/positionResolver";
import { ACTIVITIES_CONFIG } from "../pages/ballbyball/bbb-constants";
import { matchPlayer } from "../utils/playerMatch";

// ── Chip-coercion guardrail (2026-05-28) ───────────────────────────────
// Whisper sometimes mishears chip-group words ("cover drive" → "car
// drive", "off drive" → "of drive"), and the AI fallback can return
// non-canonical labels Claude invented on the fly. Without coercion
// those values flow straight into ball.analytics — the DB stores a
// string, but the on-screen chip never highlights because string
// equality fails ("car drive" !== "Cover Drive"). Operator sees the
// metric "set" in the monitor strip but the chip stays grey, leading
// to the founder report: "it is showing as car drive selected but
// actually nothing is selected."
//
// Strategy: before the parsed/AI updates are merged into ball state,
// each analytics field is checked against its canonical option list.
// If the proposed value matches exactly → keep. If it's close (token-
// level Levenshtein under threshold) → swap to the canonical label.
// If nothing close → drop the field entirely (no false "set" state).
function _buildFieldOptionsMap() {
  const out = {};
  for (const cfg of Object.values(ACTIVITIES_CONFIG)) {
    const field = cfg?.fieldName;
    if (!field) continue;
    let opts = [];
    if (cfg.items) opts = opts.concat(cfg.items);
    if (cfg.options) for (const list of Object.values(cfg.options)) opts = opts.concat(list);
    out[field] = opts;
  }
  return out;
}
const FIELD_OPTIONS_MAP = _buildFieldOptionsMap();

const _normalize = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();

// Standard iterative Levenshtein. Small enough strings here that the
// O(m*n) DP doesn't matter.
function _levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let v0 = new Array(b.length + 1);
  let v1 = new Array(b.length + 1);
  for (let i = 0; i <= b.length; i++) v0[i] = i;
  for (let i = 0; i < a.length; i++) {
    v1[0] = i + 1;
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      v1[j + 1] = Math.min(v1[j] + 1, v0[j + 1] + 1, v0[j] + cost);
    }
    [v0, v1] = [v1, v0];
  }
  return v0[b.length];
}

// Token-aware similarity (0 = identical, 1 = unrelated). For each
// token in the input we take its closest token in the candidate by
// normalised Levenshtein, then average. This beats raw whole-string
// Lev when token order is the same but one token has a substitution
// — e.g. "car drive" → "cover drive" beats "off drive" because the
// matching "drive" token has cost 0.
function _tokenSimilarity(a, b) {
  const at = a.split(/\s+/).filter(Boolean);
  const bt = b.split(/\s+/).filter(Boolean);
  if (!at.length || !bt.length) return 1.0;
  let total = 0;
  for (const x of at) {
    let best = 1.0;
    for (const y of bt) {
      const d = _levenshtein(x, y);
      const score = d / Math.max(1, Math.max(x.length, y.length));
      if (score < best) best = score;
    }
    total += best;
  }
  return total / at.length;
}

// Find the canonical option closest to `value`. Returns the canonical
// string from validOptions, or null if no candidate is close enough.
export function closestValidOption(value, validOptions) {
  if (value == null || !validOptions || !validOptions.length) return null;
  const want = _normalize(value);
  if (!want) return null;
  // Exact normalised match short-circuit.
  for (const opt of validOptions) {
    if (_normalize(opt) === want) return opt;
  }
  let best = null;
  let bestScore = Infinity;
  for (const opt of validOptions) {
    const score = _tokenSimilarity(want, _normalize(opt));
    if (score < bestScore) { bestScore = score; best = opt; }
  }
  // 0.5 means "tokens are wrong on average by less than half their
  // characters." Empirically: "car drive" → "Cover Drive" scores ~0.3;
  // "elephant" → any drive scores >0.85 (correctly rejected).
  if (bestScore <= 0.5) return best;
  return null;
}

// 2026-05-28 — runs vocabulary split into TWO categories.
//
// Founder report: bare numbers ("3" / "five" / "1") were too ambiguous —
// the same word could be a run count, a pitch cell index, an impact
// cell index, or a wagon zone. New rule: a number ONLY sets runs when
// the utterance also contains the literal word "run" or "runs" (so
// "3 runs" / "five run" / "runs 1" all work, but bare "3" no longer
// touches runs).
//
// RUN_IDIOM_WORDS — cricket idioms that are unambiguous; they fire
// alone (no "run" word required). "dot ball" / "single" / "couple" /
// "boundary" / "sixer" / "maximum" never mean anything else in the
// scoring context.
const RUN_IDIOM_WORDS = [
  ["dot|nothing|nought|naught",   0],
  ["single",                      1],
  ["couple",                      2],
  ["boundary",                    4],
  ["sixer|maximum",               6],
];

// RUN_NUMBER_WORDS — numeric tokens (digits + spelled-out numbers).
// Only fire when the utterance contains \brun(s)?\b somewhere. Whisper
// homophones from STT_ALIASES (wun→one, to/too→two, fore→four, etc.)
// are belt-and-braces duplicated here so the parser still matches if
// the alias layer misses a case.
const RUN_NUMBER_WORDS = [
  ["zero|0\\b",                                   0],
  ["one|1\\b",                                    1],
  ["two|2\\b",                                    2],
  ["three|thee|tree|3\\b",                        3],
  ["four|4\\b",                                   4],
  ["five|panch|5\\b",                             5],
  ["six|sicks|sick|size|chey|6\\b",               6],
];

const WICKET_MAP = [
  ["bowled",                 "BOWLED"],
  ["caught",                 "CAUGHT"],
  ["lbw|leg before",         "LBW"],
  ["run.?out|runout",        "RUN_OUT"],
  ["stumped",                "STUMPED"],
  ["hit.?wicket|hitwicket",  "HIT_WICKET"],
  // Retired family — MORE-SPECIFIC first so "retired not out" beats
  // "retired" and "retired hurt"/"retired out" beat the bare form.
  ["retired not.?out",       "RETIRED_NOT_OUT"],
  ["retired hurt",           "RETIRED_HURT"],
  ["retired out",            "RETIRED_OUT"],
  // 2026-06-10 — three rare dismissals founder reported as hard to
  // voice-code. Order: most-specific first so "hit ball twice"
  // doesn't shadow "hit wicket".
  ["hit (the |a )?ball twice|hit ball twice|double hit", "HIT_BALL_TWICE"],
  ["handled (the )?ball|handle ball",                    "HANDLED_BALL"],
  ["mankad(?:ed)?",                                       "MANKAD"],
  ["obstructing(?: the)? field",                          "OBSTRUCTING_FIELD"],
  ["timed out|timeout (?:dismissal|wicket)",              "TIMED_OUT"],
];

// Pitch-length keywords → ZoneMatrixPicker rows (must match the labels in
// pages/ballbyball/bbb-constants.js exactly, otherwise the highlight won't
// take). Order: most-specific first.
const PITCH_LENGTH_MAP = [
  ["yorker",                            "Yorker"],
  ["full toss|fulltoss",                "Full Toss"],
  ["beamer",                            "Beamer"],
  ["good length|goodlength|length ball","Length ball"],
  ["back of (a |the )?length|back of length|backofalength", "Back of a length"],
  ["bouncer|short ball|short pitched",  "Short"],
  ["half tracker|halftracker|long hop", "Half tracker"],
  ["full",                              "Full"],
  // "short" alone — but NOT when preceded by "no " (STT often gives
  // "no short" when the operator said "no shot" in stroke context).
  // Negative lookbehind keeps the cricket meaning intact while
  // ignoring the stroke-misrecognition spillover.
  ["(?<!no )short",                     "Short"],
];

// Stump-line keywords → COLS in ZoneMatrixPicker.
// COLS = ["WOO", "OO", "OS", "MS", "LS", "DL", "WDL"]
// More specific phrases come FIRST so "wide outside off" beats "outside off"
// and "wide down leg" beats "down leg". 2026-05-25 (later): added the
// COL CODES themselves as spoken shortcuts (Krishna ask) — "ms" / "m s"
// / "em ess" all set MS. Two-letter codes are placed AFTER the long
// natural phrases so longer matches still win when both apply; and
// AFTER the multi-letter ones (WDL > DL, WOO > OO) so the more
// specific code wins on the abbreviation path too.
const STUMP_LINE_MAP = [
  // Long, descriptive phrases first
  ["wide outside off|way outside off|wayy outside off|wide of off|wide off the off",  "WOO"],
  ["outside off|outside the off|outside-off|outside the off stump|fourth stump|fifth stump", "OO"],
  ["off stump|offstump|on off|on the off",                                            "OS"],
  ["middle stump|middlestump|on the stumps|middle and off|middle stump line|on middle",  "MS"],
  ["leg stump|legstump|on leg|on the leg",                                            "LS"],
  ["down leg|downleg|leg side|down the leg",                                          "DL"],
  ["wide down leg|way down leg|wide of leg|outside leg",                              "WDL"],
  // Spoken column codes — fast shortcuts. Each pattern covers
  //   plain letters joined ("woo")
  //   letters with spaces ("w o o")
  //   spelled-out ("double u double o", "em ess")
  // Word boundaries (\b) prevent accidental matches inside longer
  // natural phrases like "outside off" — "oo" only fires when it
  // appears as its own token. Multi-letter codes (WDL / WOO) come
  // BEFORE two-letter ones so the prefix conflict resolves correctly.
  ["wdl|w d l|double u dee el",                                  "WDL"],
  ["dl|d l|dee el",                                              "DL"],
  ["woo|w o o|double u double o",                                "WOO"],
  ["os|o s|oh ess",                                              "OS"],
  ["ms|m s|em ess",                                              "MS"],
  ["ls|l s|el ess",                                              "LS"],
  ["oo|o o|double o",                                            "OO"],
];

// Wagon-zone keywords → representative (sliceIndex, bandIndex) pairs in the
// underlying 48×6 grid. Each value is { slice, band, position } so we can
// populate structured_region_id directly. Slice index goes clockwise from 0
// (= straight back to bowler) at 7.5° each.
// Right-hander default: leg side is slices 24-47, off side is 0-23.
// MORE-SPECIFIC first so "deep mid wicket" beats "mid wicket" and
// "wide long on" beats "long on". STT often mangles fielding terms
// ("midweek" / "mid wikit" for mid wicket; "third man" → "thurd man"
// / "deep third"; "long stop" rarely spoken). Variants below cover
// the common slips Krishna's seen.
const WAGON_POSITION_MAP = [
  // Deep variants first (most specific)
  ["wide long on",                                              { slice: 41, band: 4, position: "Wide Long On" }],
  ["long off",                                                  { slice: 5,  band: 5, position: "Long Off" }],
  ["long on",                                                   { slice: 43, band: 5, position: "Long On" }],
  ["deep extra cover|deep cover",                               { slice: 8,  band: 5, position: "Deep Extra Cover" }],
  ["sweeper cover|sweeper",                                     { slice: 9,  band: 5, position: "Sweeper Cover" }],
  ["deep point|deep cover point",                               { slice: 11, band: 5, position: "Deep Point" }],
  ["deep backward point",                                       { slice: 13, band: 5, position: "Deep Point" }],
  ["backward point",                                            { slice: 12, band: 1, position: "Backward Point" }],
  ["third man|deep third|deep third man|thurd man",             { slice: 13, band: 5, position: "Third Man" }],
  ["fine third(?: man)?|short third",                           { slice: 21, band: 5, position: "Fine Third Man" }],
  ["deep fine leg|fine leg|deep fineleg",                       { slice: 25, band: 5, position: "Deep Fine Leg" }],
  ["very fine leg",                                             { slice: 22, band: 5, position: "Very Fine Leg" }],
  ["leg slip",                                                  { slice: 19, band: 0, position: "Leg Slip" }],
  ["deep backward square leg|deep backward square|backward square leg",
                                                                { slice: 29, band: 5, position: "Deep Backward Square Leg" }],
  ["deep square leg",                                           { slice: 31, band: 5, position: "Deep Square Leg" }],
  ["square leg",                                                { slice: 31, band: 1, position: "Square Leg" }],
  ["short leg",                                                 { slice: 27, band: 0, position: "Short Leg" }],
  ["deep mid wicket|deep midwicket|deep mid wikit|deep midweek",{ slice: 35, band: 5, position: "Deep Mid Wicket" }],
  ["cow corner",                                                { slice: 39, band: 5, position: "Deep Mid Wicket" }],
  ["mid wicket|midwicket|mid wikit|midweek",                    { slice: 35, band: 1, position: "Mid Wicket" }],
  // Inner-ring positions
  ["mid off",                                                   { slice: 5,  band: 1, position: "Mid Off" }],
  ["mid on",                                                    { slice: 43, band: 1, position: "Mid On" }],
  ["extra cover|cover",                                         { slice: 7,  band: 1, position: "Cover" }],
  ["point",                                                     { slice: 10, band: 1, position: "Point" }],
  ["gully",                                                     { slice: 14, band: 1, position: "Gully" }],
  // Slip cordon
  ["slip(?:s)?",                                                { slice: 18, band: 0, position: "Slip" }],
  // Bowler / keeper as fielders (catches off the bowler / behind)
  ["caught (and|n) bowled|return catch",                        { slice: 1,  band: 0, position: "Bowler" }],
  ["keeper(?: take)?|behind the stumps",                        { slice: 0,  band: 0, position: "Wicket Keeper" }],
];

// Stroke vocabulary — most-specific first so "reverse sweep" beats "sweep".
// Maps voice phrase -> exact label in ACTIVITIES_CONFIG.Stroke.
// 2026-05-25: bare "padded" routed to CONNECTION → Hit Pad per
// Krishna ask; "padded away" still maps here (the deliberate pad-as-
// shot defensive play).
const STROKE_MAP = [
  ["forward defen[cs]e|front foot defen[cs]e", "Forward Defence"],
  ["backward defen[cs]e|back foot defen[cs]e", "Backward Defence"],
  ["padded away",                   "Padded"],
  ["fend(ed)?|fended off",          "Fended"],
  ["leave(d)? alone|leaves it",     "Leave"],
  ["no shot",                       "No shot"],
  ["straight drive",                "Straight Drive"],
  ["off drive",                     "Off Drive"],
  ["on drive",                      "On Drive"],
  ["cover drive",                   "Cover Drive"],
  ["square drive",                  "Square Drive"],
  ["reverse sweep",                 "Reverse Sweep"],
  ["slog sweep",                    "Slog Sweep"],
  ["paddle sweep",                  "Paddle Sweep"],
  ["sweep",                         "Sweep"],
  ["reverse scoop",                 "Reverse Scoop"],
  ["paddle scoop",                  "Paddle Scoop"],
  ["scoop",                         "Scoop"],
  ["switch hit",                    "Switch Hit"],
  ["upper cut",                     "Upper Cut"],
  ["square cut",                    "Square Cut"],
  ["late cut",                      "Late Cut"],
  ["cut",                           "Cut"],
  ["pulled|pull shot|pull",         "Pull"],
  ["hook(ed)? shot|hook",           "Hook"],
  ["flick",                         "Flick"],
  ["slog",                          "Slog"],
  ["worked it|worked",              "Worked"],
  ["push(ed)?",                     "Pushed"],
  ["steer(ed)?",                    "Steer"],
  ["dropped",                       "Dropped"],
  ["glance",                        "Glance"],
  ["guide(d)?",                     "Guided"],
];

// Foot movement vocabulary
// "no movement" used to also map here, but it collides with the same
// phrase in DELIVERY_TYPE_MAP — operator says "no movement" intending
// the bowler's seam (delivery type) and the foot field flips too.
// Now require an explicit "foot" qualifier or "stayed still" for foot.
const FOOT_MAP = [
  ["down the (track|wicket)",       "Down the Track"],
  ["backs? away",                   "Backs Away"],
  ["sway(s|ed)? away",              "Swayed Away"],
  ["duck(s|ed)?",                   "Ducked"],
  ["deep in (the )?crease",         "Deep in Crease"],
  ["moves? in.?front",              "Moves Infront"],
  ["moves? off.?side",              "Moves Offside"],
  ["moves? leg.?side",              "Moves Legside"],
  ["front foot",                    "Front Foot"],
  ["back foot",                     "Back Foot"],
  ["no foot movement|didn'?t move (the )?feet|feet didn'?t move|stayed (still|put)|rooted to (the )?spot",
                                    "No Movement"],
];

// Shot connection vocabulary
// 2026-05-25 (Krishna ask): "padded" / "parked" → Hit Pad (was Padded).
// Bare "padded" is what STT gives for "hit the pad" in normal commentary;
// the deliberate stroke-Padded is reachable via "padded away" (STROKE_MAP).
const CONNECTION_MAP = [
  ["play(ed)? and miss(ed)? leg.?side|miss(ed)? down leg", "Play and Miss (Legside)"],
  ["play(ed)? and miss(ed)?|beat(en)?|missed it", "Play and Miss"],
  ["shoulder(ed)? arms",            "Shoulder Arms"],
  ["bat.?pad",                      "Bat-pad"],
  ["padded|parked|hit (on |the )?pad", "Hit Pad"],
  ["hit (on |the )?body",           "Hit Body"],
  ["hit (the )?helmet",             "Hit Helmet"],
  ["glov(ed|e)",                    "Gloved"],
  ["outside edge|edged outside",    "Outside Edge"],
  ["inside edge|edged inside",      "Inside Edge"],
  ["top edge|edged top",            "Top Edge"],
  ["bottom edge|edged bottom",      "Bottom Edge"],
  ["leading edge",                  "Leading Edge"],
  ["thick edge",                    "Thick Edge"],
  ["splic(ed|e)",                   "Spliced"],
  ["middled|middle of (the )?bat|sweet spot", "Middled"],
  ["well timed|timed well",         "Well Timed"],
  ["mis.?timed|miscued|mistimed",   "Mis-timed"],
  ["left it|left alone",            "Left"],
  ["normal contact",                "Normal"],
];

// Bowler plan vocabulary — kept for legacy callers; the Performance-tab
// "Bowler plan" field was removed from the UI 2026-05-23 so this no
// longer wires into ball.analytics by default. Left in case a future
// admin / scorecard reintroduces it.
const BOWLER_PLAN_MAP = [
  ["pin (to|the) pad",              "Pin to pad"],
  ["set up off.?side",              "Set up off-side"],
  ["set up leg.?side",              "Set up leg-side"],
  ["dot pressure|build pressure",   "Dot pressure"],
  ["wicket.?taking|attacking line", "Wicket-taking"],
];

// ---------------------------------------------------------------------------
// Performance-metric vocabularies. Each entry: [regex-pattern, label].
// Labels MUST match ACTIVITIES_CONFIG options EXACTLY (case + spaces +
// hyphens) — the ActivityAccordion renders chips off those strings.
//
// More specific phrases come first so longer variants beat shorter ones
// inside the same array.
// ---------------------------------------------------------------------------

// Delivery type — auto-routed based on the chosen label (PACE_DELIVERIES
// vs SPIN_DELIVERIES) by the existing bowlerTypeFromDelivery helper.
const DELIVERY_TYPE_MAP = [
  // Spin — most specific first so "reverse outswinger" beats "outswinger"
  ["off break|offbreak|offie",                              "Off Break"],
  ["left arm orthodox|orthodox spin|orthodox",              "Left arm Orthodox"],
  ["arm ball",                                              "Arm Ball"],
  ["carrom ball|carom ball",                                "Carrom Ball"],
  ["doosra|doo[s]?ra",                                      "Doosra"],
  ["slider",                                                "Slider"],
  ["leg spinner|leg break|legbreak|leggie",                 "Leg Spinner"],
  ["googly|wrong un|wrong one|chinaman",                    "Googly"],
  ["drifter|with the arm",                                  "Drifter"],
  ["top spinner|topspinner|top spin",                       "Top Spinner"],
  ["flipper",                                               "Flipper"],
  ["quicker ball",                                          "Quicker Ball"],
  // Pace — reverse first
  ["reverse in.?swinger|reverse inswing",                   "Reverse Inswinger"],
  ["reverse out.?swinger|reverse outswing",                 "Reverse Outswinger"],
  ["in.?swinger|inswing|in.?swing",                         "In Swinger"],
  ["out.?swinger|outswing|out.?swing",                      "Out Swinger"],
  ["seam in|seams in",                                      "Seam In"],
  ["seam away|seams away|seam out",                         "Seam Away"],
  ["angled in|angling in",                                  "Angled In"],
  ["angled away|angling away|angled out",                   "Angled Away"],
  ["knuckle ball|knuckler",                                 "Knuckle Ball"],
  ["off cutter|off-cutter|of cutter",                       "Off Cutter"],
  ["leg cutter|leg-cutter",                                 "Leg Cutter"],
  ["back of hand|back of the hand",                         "Back of Hand"],
  ["slower (one|ball|delivery)",                            "Slower Ball"],
  ["no movement|straight ball|hits the seam straight",      "No Movement"],
];

// Spin Direction — separate field from Delivery Type; used in the
// Spin-Direction chip group (relevant=SPIN). Patterns intentionally
// distinct from DELIVERY_TYPE_MAP so saying "off spin" sets
// spin_direction=OFF_SPIN without clobbering delivery_type.
const SPIN_DIRECTION_MAP = [
  ["off spin|offspin|spinning in to right.?hander",         "OFF_SPIN"],
  ["leg spin|legspin|spinning away from right.?hander",     "LEG_SPIN"],
  ["top spin|topspin",                                      "TOP_SPIN"],
  ["slider",                                                "SLIDER"],
  ["wrong un|wrong one|googly direction",                   "WRONG_UN"],
  ["arm ball direction|with the arm",                       "ARM_BALL"],
  ["carrom",                                                "CARROM"],
  ["doosra",                                                "DOOSRA"],
];

const DRIFT_MAP = [
  ["drift(s|ed)? in|drifting in|in.?drift",                 "IN"],
  ["drift(s|ed)? (out|away)|drifting (out|away)|out.?drift", "OUT"],
  ["no drift|straight through air|no air movement",         "NONE"],
];

const TURN_MAP = [
  ["big turn|sharp turn|turns big|turns sharply|huge turn", "BIG"],
  ["small turn|slight turn|little turn|bit of turn",        "SMALL"],
  ["straight on|no turn|skids through(?! low)|went straight",  "STRAIGHT"],
  ["skid(s|ded)?( low)?|skids low|skidder",                 "SKID"],
];

// 2026-05-25: added "left side shift" alias under Leg-side-shift —
// STT routinely renders "leg side" as "left side". Same fix on the
// off side is unneeded ("off" is rarely misheard once the global
// alias pass runs).
const KEEPER_MAP = [
  ["standard keeper|normal keeper|keeper standard|keeper back|standard",  "Standard"],
  ["up to (the )?stumps|up to stumps|up to (the )?stems|keeper up|stand(s|ing)? up", "Up-to-stumps"],
  ["leg.?side shift|left.?side shift|keeper leg side|keeper left side|keeper leg.?shift",  "Leg-side-shift"],
  ["off.?side shift|keeper off side|keeper off.?shift",                   "Off-side-shift"],
];

const BOUNCE_MAP = [
  ["extra bounce|steep bounce|bounces (extra |high|steep)|bounced (extra|high|steep)",  "EXTRA"],
  ["standard bounce|normal bounce|usual bounce|standard",            "STANDARD"],
  ["kept low|low bounce|skidded low|stayed low|stay(s|ed)? low|shooter", "KEPT_LOW"],
  ["variable bounce|uneven bounce|inconsistent bounce",               "VARIABLE"],
];

const AIR_MOVEMENT_MAP = [
  ["along the ground|on the ground|ground shot|grounded",   "Ground"],
  ["flat|flat shot|hit flat",                               "Flat"],
  ["loopy|loops|loopy shot|in the air loopy",               "Loopy"],
  ["high|hit high|high in the air|skied",                   "High"],
];

const CONTROL_MAP = [
  ["air controlled|in control|controlled shot|fully controlled|controlled",   "Air Controlled"],
  ["uncontrolled|lost control|not in control|out of control|un controlled|un-?controlled", "Un Contolled"],
];

// ── Grid-by-number lookup for the spatial matrices ─────────────────
// Founder ask 2026-05-26: number the rows/columns on the UI and let
// operators say "3 5" / "row 3 column 5" / "3 by 5" to pick a cell.
// Order matches ZoneMatrixPicker's ROWS / COLS / HEIGHTS arrays.
const PITCH_ROW_BY_NUMBER = [
  "Beamer",         // 1
  "Full Toss",      // 2
  "Yorker",         // 3
  "Full",           // 4
  "Length ball",    // 5
  "Back of a length", // 6
  "Short",          // 7
  "Half tracker",   // 8
];
const HEIGHT_ROW_BY_NUMBER = [
  "Head",      // 1
  "Shoulder",  // 2
  "Chest",     // 3
  "Waist",     // 4
  "Thigh",     // 5
  "Knee",      // 6
  "Shin",      // 7
  "Yorker",    // 8
];
const COL_BY_NUMBER = ["WOO", "OO", "OS", "MS", "LS", "DL", "WDL"];

// Spoken number → integer. "three" / "3" / "three five" all parse to ints.
const _NUM_WORD = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6,
  seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11,
};
function _parseSpokenNumber(tok) {
  if (!tok) return null;
  const n = parseInt(tok, 10);
  if (!isNaN(n)) return n;
  return _NUM_WORD[tok.toLowerCase()] ?? null;
}


// ── LOOSE maps for the BARE-OPTION-under-scope path ─────────────────
// Founder report 2026-05-26: under scope=bounce, "bounce extra" works
// but bare "extra" doesn't fire. Same for drift / turn / control /
// spin / air / keeper. Solution: a separate loose map per metric that
// matches single-word options, gated by the scope. Avoids false-fires
// in free-form utterances ("extra" alone could mean "extra cover", etc).
const BOUNCE_LOOSE_MAP = [
  ["extra",     "EXTRA"],
  ["standard|normal",  "STANDARD"],
  ["kept low|low|skidded|shooter",  "KEPT_LOW"],
  ["variable|uneven|inconsistent",  "VARIABLE"],
];
const DRIFT_LOOSE_MAP = [
  ["in|inward",   "IN"],
  ["out|away|outward",  "OUT"],
  ["none|no|nothing|no movement",  "NONE"],
];
const TURN_LOOSE_MAP = [
  ["big|sharp|huge",        "BIG"],
  ["small|slight|little|bit",  "SMALL"],
  ["straight|none|no turn",  "STRAIGHT"],
  ["skid|skidded|skids",     "SKID"],
];
const CONTROL_LOOSE_MAP = [
  ["controlled|control|in control",      "Air Controlled"],
  ["uncontrolled|no control|out of control",  "Un Contolled"],
];
const SPIN_DIRECTION_LOOSE_MAP = [
  ["off",       "OFF_SPIN"],
  ["leg",       "LEG_SPIN"],
  ["top",       "TOP_SPIN"],
  ["slider",    "SLIDER"],
  ["wrong",     "WRONG_UN"],
  ["arm",       "ARM_BALL"],
  ["carrom",    "CARROM"],
  ["doosra",    "DOOSRA"],
];
const AIR_LOOSE_MAP = [
  ["ground|grounded",  "Ground"],
  ["flat",             "Flat"],
  ["loopy|loop",       "Loopy"],
  ["high|skied",       "High"],
];
const KEEPER_LOOSE_MAP = [
  ["standard|normal|back",    "Standard"],
  ["up|forward|stumps",       "Up-to-stumps"],
  ["leg|legside|left",        "Leg-side-shift"],
  ["off|offside",             "Off-side-shift"],
];

// ---------------------------------------------------------------------------
// HEIGHT IMPACT — second matrix in ZoneMatrixPicker.
// ROWS = HEIGHTS = ["Head", "Shoulder", "Chest", "Waist", "Thigh", "Knee", "Shin", "Yorker"]
// COLS = same line vocabulary as stump_zone (shared with batter_stump_zone).
// When a HEIGHT phrase fires, the stump-line scan below ALSO runs against
// batter_stump_zone so the cell highlights cleanly — voice goes from
// "chest high on middle" → impact = (Chest, MS) in one utterance.
// ---------------------------------------------------------------------------
const HEIGHT_MAP = [
  ["head high|head height|hit (the |on the )?head",                "Head"],
  ["shoulder high|shoulder height|shoulder line|on the shoulder",  "Shoulder"],
  ["chest high|chest height|chest line|on the chest",              "Chest"],
  ["waist high|waist height|waist line|stomach high|stomach line", "Waist"],
  ["thigh high|thigh height|thigh line|on the thigh",              "Thigh"],
  ["knee high|knee height|knee roll|hit (the )?knee",              "Knee"],
  ["shin high|shin height|hit (the )?shin",                        "Shin"],
  ["boot high|yorker height|at the boots|toe height|hit (the )?boot",   "Yorker"],
];

// Looser variants used ONLY under explicit "impact" scope — bare body
// parts work because the operator has already declared the field.
const HEIGHT_LOOSE_MAP = [
  ["head",     "Head"],
  ["shoulder", "Shoulder"],
  ["chest",    "Chest"],
  ["waist|stomach|tummy",   "Waist"],
  ["thigh",    "Thigh"],
  ["knee",     "Knee"],
  ["shin",     "Shin"],
  ["boot|toe|yorker", "Yorker"],
];

// LOOSE stump-line map used ONLY under explicit "impact" or "pitch"
// scope. Bare directional words ("middle", "off", "leg", "down leg")
// are allowed because the operator has already declared the field.
// Outside scope the strict STUMP_LINE_MAP applies — required to avoid
// "off drive" / "leg glance" stroke phrases falsely setting a line.
// Founder report 2026-05-27 ("height impact not getting selected"):
// "impact chest middle" set height_zone="Chest" but batter_stump_zone
// stayed null because bare "middle" wasn't in STUMP_LINE_MAP, and the
// picker highlight requires BOTH.
const STUMP_LINE_LOOSE_MAP = [
  ["wide outside off|wide of off",  "WOO"],
  ["wide down leg|wide of leg",     "WDL"],
  ["outside off|fourth stump|fifth stump", "OO"],
  ["down leg",                       "DL"],
  ["off",                            "OS"],
  ["middle",                         "MS"],
  ["leg",                            "LS"],
];

// Match-state ("non-ball") commands — bowler / batter / keeper changes,
// innings transitions, breaks, DLS. Parsed BEFORE the ball-state grammar
// so they can short-circuit without colliding with run-word fragments.
const STATE_BREAK_MAP = [
  [/^drinks\b/,                      "Drinks Break"],
  [/^(strategic )?time.?out\b/,      "Strategic Timeout (T20)"],
  [/^lunch\b/,                       "Lunch (Test matches)"],
  [/^tea\b/,                         "Tea (Test matches)"],
  [/^(rain|weather)( delay)?\b/,     "Rain / Weather Delay"],
  [/^bad light\b/,                   "Bad Light Delay"],
  [/^(injury|medical timeout)\b/,    "Player Injury / Medical Timeout"],
  [/^drs\b|^umpire review\b/,        "DRS / Umpires Review Pause"],
];

function parseStateCommand(t) {
  // Player-targeted: "next bowler X" / "bowler X is in" / "X to bowl"
  let m;
  m = /^(?:next |new )?bowler[, ]+([a-z][a-z .'-]+?)(?:$|\s+(?:is|now|to bowl))/.exec(t);
  if (m) return { kind: "set-bowler", name: m[1].trim() };
  m = /^([a-z][a-z .'-]+?)\s+to bowl\b/.exec(t);
  if (m) return { kind: "set-bowler", name: m[1].trim() };

  m = /^(?:next |new )?(?:batter|batsman|striker)[, ]+([a-z][a-z .'-]+?)(?:$|\s+(?:is|now|on strike))/.exec(t);
  if (m) return { kind: "set-striker", name: m[1].trim() };
  m = /^([a-z][a-z .'-]+?)\s+on strike\b/.exec(t);
  if (m) return { kind: "set-striker", name: m[1].trim() };

  m = /^(?:next |new )?non.?striker[, ]+([a-z][a-z .'-]+?)(?:$|\s+(?:is|now))/.exec(t);
  if (m) return { kind: "set-non-striker", name: m[1].trim() };

  m = /^(?:wicket.?)?keeper[, ]+([a-z][a-z .'-]+?)(?:$|\s+(?:is|now))/.exec(t);
  if (m) return { kind: "set-keeper", name: m[1].trim() };

  // ── Wicket-fielder voice commands (2026-05-27) ────────────────────
  // After a wicket falls, the operator usually has to manually pick
  // caught_by / stumped_by / run_out_fielder_1/2 in the modal. These
  // patterns let voice do it. Each implicitly sets the matching
  // wicket_type in the handler so "caught by virat at slip" works
  // even when no prior "wicket caught" was said. Optional " at
  // <position>" / " in <position>" / " near <position>" suffixes are
  // accepted and discarded — they're commentary noise.
  m = /^caught (?:by )?([a-z][a-z .'-]+?)(?:\s+(?:at|in|near|on)\s+[a-z][a-z .'-]+?)?\s*$/.exec(t);
  if (m && !/^caught (?:by )?(behind|the)/.test(t)) {
    return { kind: "set-caught-by", name: m[1].trim() };
  }
  m = /^stumped (?:by )?([a-z][a-z .'-]+?)\s*$/.exec(t);
  if (m) return { kind: "set-stumped-by", name: m[1].trim() };
  // "run out by X" / "run out X" / "run out X and Y" / "run out X to Y"
  // / "X to Y run out". Two-fielder variants set both ends of the
  // throw. "Mankad by X" routes to dismissed_by_id (the bowler-credit
  // path) because Law 41.16 mankads aren't credited to a fielder.
  m = /^run.?out (?:by\s+)?([a-z][a-z .'-]+?)(?:\s+(?:and|to|throw to)\s+([a-z][a-z .'-]+?))?\s*$/.exec(t);
  if (m) {
    const names = [m[1].trim()];
    if (m[2]) names.push(m[2].trim());
    return { kind: "set-run-out-fielders", names };
  }
  m = /^mankad(?:ed)? (?:by\s+)?([a-z][a-z .'-]+?)\s*$/.exec(t);
  if (m) return { kind: "set-mankad-by", name: m[1].trim() };

  // ──────────────────────────────────────────────────────────────────
  // More-menu voice coverage (founder ask 2026-06-11) — every action
  // reachable from the MORE button should be voice-codable. Specific
  // patterns come BEFORE the generic innings-end / break maps so they
  // don't get mis-parsed (e.g. "end this over now" must NOT match
  // the bare "end innings" pattern below).
  // ──────────────────────────────────────────────────────────────────

  // End match — the whole match, not just innings
  if (/^(end (current |the )?match|match (over|end|finished)|finish (the )?match)\b/.test(t))
    return { kind: "end-match" };

  // Phantom-ball — End This Over Now
  if (/^(end (this |the )?over (now|here)|end over now|complete (this |the )?over)\b/.test(t))
    return { kind: "end-over-now" };

  // Phantom-ball — Add One Extra Ball
  if (/^(add (one |an |a )?(extra |phantom )?ball|phantom ball|extra ball|one (more|extra) ball)\b/.test(t))
    return { kind: "add-phantom-ball" };

  // Floodlights — ON / OFF / toggle. "lights on/off" also matches
  // because that's how operators actually say it in commentary.
  m = /^(floodlights?|lights)\s+(on|off|enable|disable)\b/.exec(t);
  if (m) {
    const verb = m[2];
    const state = (verb === "on" || verb === "enable") ? true : false;
    return { kind: "floodlights", state };
  }
  if (/^(toggle |flip )?(floodlights?|lights)\b/.test(t))
    return { kind: "floodlights", state: null };

  // Pause / Resume match scoring
  if (/^(pause (the )?match|pause scoring|stop scoring)\b/.test(t))
    return { kind: "pause-match" };
  if (/^(resume (the )?match|resume scoring|in play|continue (the )?match)\b/.test(t))
    return { kind: "resume-match" };
  if (/^toggle pause\b/.test(t))
    return { kind: "toggle-pause" };

  // Rain affected — toggle on/off (unlocks DLS section)
  if (/^(rain affected at start|rain at start|rain (-)?reduced at start)\b/.test(t))
    return { kind: "rain-affected-start" };
  if (/^(end innings rain affected|end innings (due to |for )?rain|rain affected (innings )?end)\b/.test(t))
    return { kind: "end-innings-rain" };
  m = /^rain affected\s+(on|off|enable|disable)\b/.exec(t);
  if (m) {
    const state = (m[1] === "on" || m[1] === "enable");
    return { kind: "rain-affected", state };
  }
  if (/^(rain affected|enable dls|unlock dls)\b/.test(t))
    return { kind: "rain-affected", state: true };
  if (/^(disable dls|lock dls)\b/.test(t))
    return { kind: "rain-affected", state: false };

  // Overs Remaining sub-modal
  if (/^(overs remaining|change (max )?overs|reduce overs|set overs|adjust overs|max overs)\b/.test(t))
    return { kind: "open-overs-remaining" };

  // Impact Player / Concussion Sub
  if (/^impact (player|sub|substitut(e|ion))\b/.test(t))
    return { kind: "open-impact" };
  if (/^concussion (sub|substitut(e|ion))\b/.test(t))
    return { kind: "open-concussion" };

  // Test-format day/session controls — stumps fires markTestStumps
  // (advances Day + resets Session). Lunch / Tea are also exposed
  // here so they advance the Test session counter, in addition to
  // the generic break_type they already get via STATE_BREAK_MAP.
  if (/^(stumps|close of play|end of day|day('?s)? end)\b/.test(t))
    return { kind: "test-stumps" };
  if (/^(test )?lunch( break)?\b/.test(t))
    return { kind: "test-lunch" };
  if (/^(test )?tea( break)?\b/.test(t))
    return { kind: "test-tea" };

  // Match Settings (rain / powerplay override) modal
  if (/^(match settings|open match settings|powerplay (settings|override))\b/.test(t))
    return { kind: "open-match-settings" };

  // Fielding-restriction Penalty modal
  if (/^(fielding (restriction|penalty)|open penalty)\b/.test(t))
    return { kind: "open-fielding-penalty" };

  // Innings transitions
  if (/^all out\b/.test(t))                       return { kind: "innings-end", closure_type: "ALL_OUT" };
  if (/^target reached\b|^chased( down)?\b/.test(t)) return { kind: "innings-end", closure_type: "TARGET_REACHED" };
  if (/^overs (completed|done)\b/.test(t))         return { kind: "innings-end", closure_type: "OVERS_COMPLETED" };
  if (/^declar(ed|ation)\b/.test(t))               return { kind: "innings-end", closure_type: "DECLARED" };
  if (/^(innings (change|end|over)|next innings|end (current |the )?innings)\b/.test(t))
    return { kind: "innings-end", closure_type: "" };

  // Breaks
  for (const [pat, label] of STATE_BREAK_MAP) {
    if (pat.test(t)) return { kind: "start-break", break_type: label };
  }

  // DLS — supports several shapes operators actually say:
  //   "dls revised target 142 in 17 overs"
  //   "dls target 142 in 17"
  //   "dls 142 in 17 overs"
  //   "apply dls 142 / 17" / "dls 142 / 17"
  //   "revised target 142 overs 17"
  //   "dls score" / "open dls"  →  open modal with no pre-fill
  m = /^(?:apply\s+)?dls(?:\s+(?:revised\s+)?target)?\s+(\d+)\s*(?:in|\/|for|over|overs?)\s*(\d+(?:\.\d+)?)\s*overs?\b/.exec(t);
  if (m) return { kind: "dls", target: Number(m[1]), overs: Number(m[2]) };
  m = /^revised\s+target\s+(\d+)\s+(?:in\s+)?(\d+(?:\.\d+)?)\s+overs?\b/.exec(t);
  if (m) return { kind: "dls", target: Number(m[1]), overs: Number(m[2]) };
  if (/^(dls( score| panel)?|open dls|apply dls)\b/.test(t))
    return { kind: "dls", target: null, overs: null };

  // New ball signal (Test / ODI 35-overs+)
  if (/^new ball\b/.test(t)) return { kind: "new-ball" };

  return null;
}

// STT misrecognitions → intended cricket words. Applied as whole-word
// substitutions BEFORE the parser scans, so downstream regexes don't
// need to learn each variant. Only includes substitutions that are
// SAFE: each LHS is not a meaningful English/cricket word on its own
// (e.g. "fuk", "slok", "rivers"), or its only common meaning IS the
// cricket term we're mapping to.
//
// Deliberately NOT in here (too ambiguous — would break valid meanings):
//   "short"  — also a length keyword (bouncer)
//   "of"     — extremely common English word
//   "live"   — could legitimately be "live ball"
//   "it"     — too short, matches inside every other word
//   "its"    — also very common
//   "mis"    — substring of many words
//   "left"   — could be "left arm" / "leaves it"
//   "padded" — handled by routing it to Hit Pad in CONNECTION_MAP, not by alias
const STT_ALIASES = {
  // Delivery type STT slips
  "rivers": "reverse",
  "sim":    "seam",
  "nuckle": "knuckle",
  // Stroke STT slips
  "fuk":    "hook",
  "pul":    "pull",
  "slok":   "slog",
  "scope":  "scoop",
  "stir":   "steer",
  "glands": "glance",
  "wire":   "square",
  // More homophones surfaced by Krishna's testing 2026-05-26
  "weir":   "square",
  "swear":  "square",
  "lay":    "leg",          // "lay stump" → "leg stump" (won't fire bare "lay")
  "pall":   "pull",
  "puh":    "pull",
  "hokeed": "hooked",
  "drove":  "drive",
  "smock":  "smog",         // STT mishears "slog"
  // Foot movement STT slips
  "food":   "foot",
  "bag":    "backs",
  "increase":  "in crease",
  // Keeper STT slips
  "stems":  "stumps",
  // Air movement STT slip
  "are":    "air",
  // Connection STT slips
  "tick":   "thick",
  "splashed": "spliced",
  "parked": "padded",
  "missed": "miss",
  // Number STT slip
  "torrent": "two runs",
  // ── Hindi / Hinglish cricket terms (2026-05-26 bilingual support) ─
  // Word-boundary safe substitutions. Hindi commentary mixes English
  // heavily; we only translate the terms that have a clean cricket
  // counterpart and no English-word ambiguity.
  "chauka": "four",
  "chowka": "four",
  "chakka": "six",
  "chhakka": "six",
  "gend":   "ball",          // filler — drops out of regex matches
  "gendh":  "ball",
  "aage":   "down",          // "aage badhke" → "down the track" (partial)
  "badhke": "the track",
  "pichhe": "back",          // "pichhe hatke" → "back away"
  "hatke":  "away",
  "vikit":  "wicket",
  "vikkit": "wicket",
  "achha":  "good",          // "achha shot" — drops to "good shot"
  "achhha": "good",
  "shaandar": "great",
  "yorker-ball": "yorker",
  "off-ka": "off",
  "leg-ka": "leg",
  // ── Round 2 expansion 2026-05-26 — predicted-misrecognition pass
  // before Krishna's team data lands. Each entry was reasoned from
  // STT phonetic-similarity (Indian-accent English on Whisper +
  // browser) against the cricket vocabulary. Trim or extend based
  // on the real "(said → heard)" log.
  // Delivery type
  "in-swinger":     "inswinger",  // some STT engines hyphenate
  "out-swinger":    "outswinger",
  "cuter":          "cutter",
  "knockle":        "knuckle",
  "noccle":         "knuckle",
  "googli":         "googly",
  "googley":        "googly",
  "dosra":          "doosra",
  "dushra":         "doosra",
  "armball":        "arm ball",
  "carom":          "carrom",
  "karam":          "carrom",
  "topspinner":     "top spinner",
  "leg-spinner":    "leg spinner",
  // Stroke
  "kover":          "cover",
  "cooper":         "cover",
  "covers":         "cover",
  "straight-drive": "straight drive",
  "leg-glance":     "glance",
  "pad-it":         "padded",
  "pad-on":         "padded",
  "shoulder-arms":  "shoulder arms",
  "shoulders-arms": "shoulder arms",
  "left-it":        "leave",
  "leaves":         "leave",
  // Pitch
  "yokker":         "yorker",
  "yorca":          "yorker",
  "yarker":         "yorker",
  "yokr":           "yorker",
  "bumper":         "bouncer",
  "bouncerball":    "bouncer",
  "fulltoss":       "full toss",
  "fulla":          "full",
  // Stump line
  "off-stamp":      "off stump",
  "leg-stamp":      "leg stump",
  "middle-stamp":   "middle stump",
  // Wagon
  "midwicket":      "mid wicket",
  "mid-wicket":     "mid wicket",
  "cowcorner":      "cow corner",
  "thirdman":       "third man",
  "fineleg":        "fine leg",
  "squareleg":      "square leg",
  "longoff":        "long off",
  "longon":         "long on",
  "deepcover":      "deep cover",
  "deepmidwicket":  "deep mid wicket",
  // Save / undo / cancel
  "saved":          "save",
  "save-it":        "save",
  "scored":         "save",
  "ondo":           "undo",
  "andoo":          "undo",
  "kancel":         "cancel",
  // Run counts
  "wun":            "one",
  "to":             "two",
  "too":            "two",
  "tree":           "three",
  "fore":           "four",
  "for":            "four",
  "sicks":          "six",
  "sex":            "six",
  // Wickets
  "bowled-him":     "bowled",
  "elbedablu":      "lbw",
  "el-bee-double":  "lbw",
  "elbee-double":   "lbw",
  "stomped":        "stumped",
  "stomp":          "stumped",
  "runout":         "run out",
  "manked":         "mankad",
  // 2026-06-10 — common STT mishears for the rare-dismissal vocab
  // founder reported as hard to voice-code.
  "mankaded":       "mankad",
  "min-cad":        "mankad",
  "men-cad":        "mankad",
  "monkad":         "mankad",
  "min cad":        "mankad",
  "monkadded":      "mankad",
  "hit ball twice": "hit ball twice",
  "double-hit":     "double hit",
  "doublehit":      "double hit",
  "hit balls":      "hit ball twice",   // STT plural mishear
  "handle ball":    "handled ball",
  "handled":        "handled ball",     // when standalone
  "handle the ball": "handled the ball",
  "obstructed":     "obstructing field",
  "obstructing":    "obstructing field",
  "time-out":       "timed out",
  "timeout":        "timed out",
  // Hindi extras — all expand to "<n> runs" form so the parser's
  // new "number-needs-runs-word" rule fires for Hindi operators
  // (founder 2026-05-28). "panch"/"chey"/"char-runs" used to expand
  // to bare numbers, which the new rule rejects.
  "char-runs":      "four runs",
  "chey":           "six runs",
  "panch":          "five runs",
  "do-runs":        "two runs",
  "tin-runs":       "three runs",  // "teen runs" mis-spelled

  // ── Phonetic-similar homophones (founder report 2026-05-26) ────────
  // Whisper + browser STT both mishear "off" as half / of / awf / aff;
  // and "shot" → "short", which collides with pitch_length=Short.
  // Apply word-boundary substitutions so only standalone tokens get
  // rewritten — "halfway" / "officially" stay intact.
  "of":      "off",            // "outside of" → "outside off"
  "half":    "off",            // "outside half" → "outside off"
  "awf":     "off",
  "aff":     "off",
  "ov":      "off",
  "shot":    "shot",           // explicit pass-through — see parser change
                               // (we DON'T rewrite shot→short; we narrow
                               //  the "short" regex so it can't false-fire)
  "leeg":    "leg",
  "lay":     "leg",
  "led":     "leg",
  "mid":     "middle",         // bare "mid" → "middle" in stump context

  // ── Action-button abbreviations Krishna's team uses ────────────────
  // Spoken letter codes for fast in-game tagging. They map to their
  // full phrases so the existing regex parsers pick them up.
  // 2-letter "code" aliases for the toolbar buttons. Whisper transcribes
  // spoken letters in many ways ("DB" → "dee bee" / "deebee" / "dee b"
  // / etc.); each row catches a distinct variant. ONLY entries that
  // can't false-fire from normal English are listed here — "white" /
  // "b" alone / "the bee" were tried and dropped 2026-05-28 because
  // they re-mapped legitimate English in adjacent contexts. The fast-
  // path (interim STT firing immediately for runs/extras) gives the
  // operator immediate feedback even when Whisper lands on a longer
  // canonical phrase like "no ball" or "dot ball" directly.
  // Dot ball
  "db":         "dot",
  "d b":        "dot",
  "dee bee":    "dot",
  "deebee":     "dot",
  "dee b":      "dot",
  "dot ball":   "dot",
  "dotball":    "dot",
  // Wide
  "wd":         "wide",
  "w d":        "wide",
  "double u d": "wide",
  "doubleud":   "wide",
  "double you d": "wide",
  "wyde":       "wide",
  "waid":       "wide",
  // No ball
  "nb":         "no ball",
  "n b":        "no ball",
  "en bee":     "no ball",
  "enbee":      "no ball",
  "en b":       "no ball",
  "no-ball":    "no ball",
  "noball":     "no ball",
  // Leg bye
  "lb":         "leg bye",
  "l b":        "leg bye",
  "el bee":     "leg bye",
  "elbee":      "leg bye",
  "el b":       "leg bye",
  "leg-bye":    "leg bye",
  "legbye":     "leg bye",
  // Bye — bare "b" / "by" too risky (false-fire on English "by"); operator
  // can say the full word "bye"/"byes" and the parser handles it.
  "bye":        "bye",
  "byes":       "bye",
  // Short run
  "sr":         "short run",
  "s r":        "short run",
  "ess are":    "short run",
  "essar":      "short run",
  "es are":     "short run",
  "short-run":  "short run",
  "shortrun":   "short run",
  // Quick run
  "qr":         "quick run",
  "q r":        "quick run",
  "qu are":     "quick run",
  "kew are":    "quick run",
  "queue are":  "quick run",
  "queuer":     "quick run",
  // Free hit
  "fh":         "free hit",
  "f hit":      "free hit",
  "fhit":       "free hit",
  "f-hit":      "free hit",
  "ef hit":     "free hit",
  "ef-hit":     "free hit",
  "f h":        "free hit",
  "free-hit":   "free hit",
  "freehit":    "free hit",
  // DRS — phonetic variants Whisper produces for "D-R-S"
  "dr s":       "drs",
  "deers":      "drs",
  "deer s":     "drs",
  "dee r s":    "drs",
  "dee are ess":"drs",
  "deeares":    "drs",

  // ── Founder-supplied vocab expansion 2026-05-27 ──────────────────────
  // Hand-collected pronunciation / spelling variants for action buttons,
  // run amounts, extras, keeper / bounce / delivery / stroke / foot
  // movement / air movement labels. Curated from a ~3000-entry list —
  // I dropped mixed-script ("sिक्स"), random-typo-only ("twwo"), and
  // cricket-commentary alt phrasings ("got the wicket", "important
  // wicket") that belong in the parser instead of the alias layer.
  // Auto-promote pipeline (extendSttAliases ← server-mined corrections)
  // keeps adding real (heard, intended) pairs on top of this.
  //
  // ── Action buttons ──
  // Note: the parser below (parse() → token-based clip-in/out detection)
  // is the primary path. These aliases only short-circuit the most common
  // single-token variants. Keep aliases TIGHT (no "clipping" → "clip in"
  // — Whisper sometimes emits "clipping in" which would double-expand to
  // "clip in in" and confuse the matcher).
  "clipin":         "clip in",
  "clippin":        "clip in",
  "clipped in":     "clip in",
  "clip inn":       "clip in",
  "klipin":         "clip in",
  "klippin":        "clip in",
  "kleep in":       "clip in",
  "klip in":        "clip in",
  "cleep in":       "clip in",
  "cleepin":        "clip in",
  "click in":       "clip in",
  "click-in":       "clip in",
  "clipout":        "clip out",
  "klipout":        "clip out",
  "klip out":       "clip out",
  "kleep out":      "clip out",
  "cleep out":      "clip out",
  "cleepout":       "clip out",
  "click out":      "clip out",
  "click-out":      "clip out",
  "saveball":       "save ball",
  "save-ball":      "save ball",
  "saved ball":     "save ball",
  "safe ball":      "save ball",
  "safeball":       "save ball",
  "saiv ball":      "save ball",
  "sev ball":       "save ball",
  "save bowl":      "save ball",
  "save bawl":      "save ball",
  "editball":       "edit ball",
  "edit-ball":      "edit ball",
  "edit bowl":      "edit ball",
  "edit bawl":      "edit ball",
  "modify ball":    "edit ball",
  "correct ball":   "edit ball",
  "fix ball":       "edit ball",
  "redo":           "redo",
  "re-do":          "redo",
  "reedo":          "redo",
  "redoo":          "redo",
  "repeat":         "redo",
  "do again":       "redo",
  "qcee":           "qc",
  "qsi":            "qc",
  "quick check":    "qc",
  "quality check":  "qc",
  "pitchmap":       "pitch map",
  "pitch-map":      "pitch map",
  "pitch chart":    "pitch map",
  "pitch grid":     "pitch map",
  "bowling map":    "pitch map",
  "heightimpact":   "height impact",
  "height-impact":  "height impact",
  "impact map":     "height impact",
  "impact zone":    "height impact",

  // ── Wicket variants ──
  "viket":          "wicket",
  "vicket":         "wicket",
  "wikket":         "wicket",
  "wickett":        "wicket",
  "wikit":          "wicket",
  "wicked":         "wicket",
  "vikked":         "wicket",
  "wickit":         "wicket",
  "wiccet":         "wicket",

  // ── Dot ball / zero runs ──
  "dotball":        "dot ball",
  "dot-ball":       "dot ball",
  "dott ball":      "dot ball",
  "no run":         "dot",
  "norun":          "dot",
  "no-run":         "dot",
  "no runs":        "dot",
  "dawt":           "dot",
  "dott":           "dot",
  "daught":         "dot",
  "doot":           "dot",
  "maiden ball":    "dot",
  "maiden delivery": "dot",

  // ── Number STT mishears (numerals 1–10 — covers run counts) ──
  "won":            "one",
  "wan":            "one",
  "wun-one":        "one",
  "single":         "one",
  "singal":         "one",
  "sengle":         "one",
  "doo":            "two",
  "tew":            "two",
  "dwo":            "two",
  "double":         "two",
  "dobble":         "two",
  "dubble":         "two",
  "thri":           "three",
  "thry":           "three",
  "thrri":          "three",
  "thray":          "three",
  "tri":            "three",
  "triple":         "three",
  "tripple":        "three",
  "foor":           "four",
  "phore":          "four",
  "foar":           "four",
  "fiv":            "five",
  "faiv":           "five",
  "fyve":           "five",
  "phive":          "five",
  "fife":           "five",
  "foive":          "five",
  "siks":           "six",
  "seks":           "six",
  "sax":            "six",
  "sixer":          "six",
  "maximum":        "six",
  "sevan":          "seven",
  "sevun":          "seven",
  "siven":          "seven",
  "ate":            "eight",
  "eit":            "eight",
  "aight":          "eight",
  "nain":           "nine",
  "nyne":           "nine",
  "nein":           "nine",
  "tin":            "ten",
  "den":            "ten",

  // ── Wide ──
  "waid":           "wide",
  "wyde":           "wide",
  "whide":          "wide",
  "wideball":       "wide",
  "wide-ball":      "wide",
  "wide ball":      "wide",

  // ── No ball ──
  "noball":         "no ball",
  "no-ball":        "no ball",
  "no bawl":        "no ball",
  "no bowl":        "no ball",
  "nobol":          "no ball",
  "nobal":          "no ball",
  "kno ball":       "no ball",

  // ── Byes / leg byes ──
  "legbye":         "leg bye",
  "legbyes":        "leg bye",
  "leg-bye":        "leg bye",
  "leg-byes":       "leg bye",
  "byee":           "bye",
  "biye":           "bye",
  "bais":           "bye",

  // ── Short run / quick run / free hit / DRS ──
  "shortrun":       "short run",
  "short-run":      "short run",
  "one short":      "short run",
  "quickrun":       "quick run",
  "quick-run":      "quick run",
  "quick single":   "quick run",
  "sharp single":   "quick run",
  "fast single":    "quick run",
  "drss":           "drs",
  "review":         "drs",
  "player review":  "drs",
  "third umpire":   "drs",
  "hawkeye":        "drs",
  "hawk eye":       "drs",
  "ultra edge":     "drs",

  // ── Bounce ──
  "bouns":          "bounce",
  "baunce":         "bounce",
  "bownce":         "bounce",
  "bounse":         "bounce",
  "standerd":       "standard",
  "standart":       "standard",
  "stenderd":       "standard",
  "stannard":       "standard",
  "kept lo":        "kept low",
  "keptlow":        "kept low",
  "kept-low":       "kept low",
  "variabel":       "variable",
  "varible":        "variable",
  "vairable":       "variable",
  "xtra":           "extra",
  "ekstra":         "extra",

  // ── Keeper position ──
  "keaper":         "keeper",
  "keper":          "keeper",
  "kiper":          "keeper",
  "upto stumps":    "up to stumps",
  "up-to-stumps":   "up to stumps",
  "upstumps":       "up to stumps",
  "legside shift":  "leg side shift",
  "leg-side shift": "leg side shift",
  "offside shift":  "off side shift",
  "off-side shift": "off side shift",

  // ── Delivery type — pace ──
  "in swinger":     "inswinger",
  "out swinger":    "outswinger",
  "in swinga":      "inswinger",
  "out swinga":     "outswinger",
  "seamin":         "seam in",
  "seam-in":        "seam in",
  "seamaway":       "seam away",
  "seam-away":      "seam away",
  "reverse inswinger":  "reverse inswinger",
  "reverse-inswinger":  "reverse inswinger",
  "reverse outswinger": "reverse outswinger",
  "reverse-outswinger": "reverse outswinger",
  "angledin":       "angled in",
  "angled-in":      "angled in",
  "angledaway":     "angled away",
  "angled-away":    "angled away",
  "slowerball":     "slower ball",
  "slower-ball":    "slower ball",
  "knuckleball":    "knuckle ball",
  "knuckle-ball":   "knuckle ball",
  "offcutter":      "off cutter",
  "off-cutter":     "off cutter",
  "legcutter":      "leg cutter",
  "leg-cutter":     "leg cutter",
  "backofhand":     "back of hand",
  "back-of-hand":   "back of hand",

  // ── Delivery type — spin ──
  "spiner":         "spinner",
  "spinna":         "spinner",
  "spinnar":        "spinner",
  "noturn":         "no turn",
  "no-turn":        "no turn",
  "offbreak":       "off break",
  "off-break":      "off break",
  "leftarmorthodox":     "left arm orthodox",
  "left-arm-orthodox":   "left arm orthodox",
  "left arm ortho":      "left arm orthodox",
  "armball":        "arm ball",
  "arm-ball":       "arm ball",
  "carromball":     "carrom ball",
  "carrom-ball":    "carrom ball",
  "carom ball":     "carrom ball",
  "topspinner":     "top spinner",
  "top-spinner":    "top spinner",
  "legspinner":     "leg spinner",
  "leg-spinner":    "leg spinner",
  "quickerball":    "quicker ball",
  "quicker-ball":   "quicker ball",

  // ── Spin direction ──
  "offspin":        "off spin",
  "off-spin":       "off spin",
  "legspin":        "leg spin",
  "leg-spin":       "leg spin",
  "topspin":        "top spin",
  "top-spin":       "top spin",
  "wrongun":        "wrong un",
  "wrong-un":       "wrong un",
  "wrong one":      "wrong un",

  // ── Stroke ──
  "stroak":         "stroke",
  "strok":          "stroke",
  "defense":        "defence",
  "noshot":         "no shot",
  "no-shot":        "no shot",
  "leev":           "leave",
  "leave it":       "leave",
  "left it":        "leave",
  "forwarddefence": "forward defence",
  "forward-defence": "forward defence",
  "forward defense": "forward defence",
  "backwarddefence": "backward defence",
  "backward-defence": "backward defence",
  "backward defense": "backward defence",
  "paded":          "padded",
  "padded shot":    "padded",
  "fendid":         "fended",
  "straightdrive":  "straight drive",
  "offdrive":       "off drive",
  "off-drive":      "off drive",
  "ondrive":        "on drive",
  "on-drive":       "on drive",
  "coverdrive":     "cover drive",
  "cover-drive":    "cover drive",
  "squaredrive":    "square drive",
  "square-drive":   "square drive",
  "flik":           "flick",
  "flic":           "flick",
  "slogg":          "slog",
  "sweap":          "sweep",
  "swep":           "sweep",
  "reversesweep":   "reverse sweep",
  "reverse-sweep":  "reverse sweep",
  "slogsweep":      "slog sweep",
  "slog-sweep":     "slog sweep",
  "paddlesweep":    "paddle sweep",
  "paddle-sweep":   "paddle sweep",
  "skuup":          "scoop",
  "switchhit":      "switch hit",
  "switch-hit":     "switch hit",
  "latecut":        "late cut",
  "late-cut":       "late cut",
  "uppercut":       "upper cut",
  "upper-cut":      "upper cut",
  "squarecut":      "square cut",
  "square-cut":     "square cut",
  "pul":            "pull",
  "huk":            "hook",
  "workd":          "worked",
  "pushd":          "pushed",
  "stear":          "steer",
  "stir":           "steer",
  "droped":         "dropped",
  "glans":          "glance",

  // ── Foot movement ──
  "nomovement":     "no movement",
  "no-movement":    "no movement",
  "no movment":     "no movement",
  "frontfoot":      "front foot",
  "front-foot":     "front foot",
  "backfoot":       "back foot",
  "back-foot":      "back foot",
  "downthetrack":   "down the track",
  "down-the-track": "down the track",
  "down track":     "down the track",
  "backsaway":      "backs away",
  "backs-away":     "backs away",
  "back away":      "backs away",
  "duked":          "ducked",
  "duckt":          "ducked",
  "swayedaway":     "swayed away",
  "swayed-away":    "swayed away",
  "deepincrease":   "deep in crease",
  "deep-in-crease": "deep in crease",
  "deep crease":    "deep in crease",
  "movesinfront":   "moves infront",
  "moves in front": "moves infront",
  "movesoffside":   "moves offside",
  "moves off side": "moves offside",
  "moveslegside":   "moves legside",
  "moves leg side": "moves legside",

  // ── Air movement ──
  "loopi":          "loopy",
  "loopie":         "loopy",
  "luupy":          "loopy",
  "flatt":          "flat",
  "flaat":          "flat",
  "haai":           "high",
  "hi":             "high",
  "graund":         "ground",
  "grownd":         "ground",
};

let STT_ALIAS_PATTERN = buildSttAliasPattern();

function buildSttAliasPattern() {
  return new RegExp(
    "\\b(" + Object.keys(STT_ALIASES).map(
      // Escape regex metachars in keys (none currently, but future-proof).
      (k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    ).join("|") + ")\\b",
    "gi",
  );
}

// T126: server-mined corrections auto-promote into the alias dict.
// The Django `mine_voice_corrections` command outputs candidate
// (heard, intended) pairs from VoiceUtterance.operator_correction;
// the frontend fetches them at mount and calls extendSttAliases to
// merge them into the live regex without a code change. Manual hand-
// curated entries above always win — that's why we only set keys
// that aren't already present.
export function extendSttAliases(extra) {
  if (!extra || typeof extra !== "object") return 0;
  let added = 0;
  for (const [k, v] of Object.entries(extra)) {
    if (typeof k !== "string" || typeof v !== "string") continue;
    const key = k.toLowerCase().trim();
    const val = v.toLowerCase().trim();
    if (!key || !val || key === val) continue;
    if (STT_ALIASES[key]) continue;          // hand-curated wins
    STT_ALIASES[key] = val;
    added += 1;
  }
  if (added > 0) {
    STT_ALIAS_PATTERN = buildSttAliasPattern();
  }
  return added;
}

function normalizeSttAliases(text) {
  // Run a single whole-word pass so collisions resolve left-to-right
  // (first match wins; we don't have overlapping LHSes in the dict).
  return text.replace(STT_ALIAS_PATTERN, (m) => STT_ALIASES[m.toLowerCase()] || m);
}

// ── Voice macros ────────────────────────────────────────────────────
// User-definable shortcut phrases stored in localStorage:
//   { "kohli edge": "stroke cut, connection outside edge",
//     "bowler change": "next bowler bumrah" }
// Applied BEFORE the parser runs. The expansion is then parsed as if
// the operator had spoken it directly — so one short phrase fans out
// into a sequence of standard commands.
//
// Manage via setVoiceMacros() (UI builds on this); the parser just
// looks them up.
const MACRO_STORAGE_KEY = "zcd_voice_macros";
function loadMacros() {
  try {
    const raw = localStorage.getItem(MACRO_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
}
function applyMacros(text) {
  const macros = loadMacros();
  const keys = Object.keys(macros);
  if (keys.length === 0) return text;
  const t = text.toLowerCase().trim();
  // Exact match first (the common case: "kohli edge" maps to a full
  // ball description). Falls through to prefix match so an operator
  // can chain "<macro> <extra phrase>".
  if (macros[t]) return macros[t];
  for (const k of keys) {
    if (t.startsWith(`${k} `)) return macros[k] + t.slice(k.length);
  }
  return text;
}

export function setVoiceMacros(macros) {
  try {
    localStorage.setItem(MACRO_STORAGE_KEY, JSON.stringify(macros || {}));
  } catch { /* quota / private mode — non-fatal */ }
}
export function getVoiceMacros() { return loadMacros(); }

// SCOPE keywords — when present, the parser narrows scanning to JUST
// the named field's map(s). Operator pattern: prefix the metric name
// to disambiguate. E.g.:
//   "stroke no shot"     → only STROKE_MAP, no PITCH "short" collision
//   "wagon mid wicket"   → only WAGON_POSITION_MAP, no STROKE/foot match
//   "impact chest middle"→ only HEIGHT_MAP + STUMP_LINE→batter_stump_zone
//   "pitch good length"  → only PITCH_LENGTH_MAP + STUMP_LINE→stump_zone
// Without a scope keyword the parser falls back to scanning every map
// (the legacy free-form behaviour).
//
// Format: [pattern_regex, scope_token]. The matched chunk is STRIPPED
// from the utterance before the per-field scans run so it doesn't
// pollute the residual text.
// scope_token → which map IDs the field scans are allowed to touch.
// "wicket" / "runs" / "extras" / "boundary" stay UNCONDITIONAL — they're
// useful in every context and there's no realistic collision risk.
const SCOPE_TO_MAPS = {
  stroke:     ["STROKE"],
  delivery:   ["DELIVERY_TYPE"],
  spin:       ["SPIN_DIRECTION"],
  drift:      ["DRIFT"],
  turn:       ["TURN"],
  keeper:     ["KEEPER"],
  bounce:     ["BOUNCE"],
  foot:       ["FOOT"],
  connection: ["CONNECTION"],
  air:        ["AIR_MOVEMENT"],
  control:    ["CONTROL"],
  pitch:      ["PITCH_LENGTH", "STUMP_LINE_PITCH"],
  impact:     ["HEIGHT", "STUMP_LINE_IMPACT"],
  wagon:      ["WAGON_POSITION"],
  // Commentary scope: NO map is allowed to fire — the residual text
  // becomes free-form commentary on the ball. See parse() below.
  commentary: [],
};

const SCOPE_KEYWORDS = [
  ["stroke|shot played",                          "stroke"],
  ["delivery (type)?|ball type",                  "delivery"],
  ["spin direction|spin",                         "spin"],
  ["drift direction|drift",                       "drift"],
  ["turn (after pitch)?|rotation",                "turn"],
  ["keeper position|keeper|wicket.?keeper",       "keeper"],
  ["bounce category|bounce",                      "bounce"],
  ["foot movement|footwork|foot",                 "foot"],
  ["shot connection|connection|contact",          "connection"],
  ["air movement|air",                            "air"],
  ["control (level)?|controlled?",                "control"],
  ["pitch (map|zone)?|pitching|landed",           "pitch"],
  ["height impact|impact|hit at|hits at",         "impact"],
  ["wagon (wheel)?|played to|fielded (by|at)|hit to|towards", "wagon"],
  // Commentary: whatever follows the keyword is the commentary text.
  // No metric scans run; the residual phrase is stored verbatim.
  ["commentary|comment|note|notes|remark",        "commentary"],
];

function detectScope(t) {
  for (const [pat, scope] of SCOPE_KEYWORDS) {
    const re = new RegExp(`(^|\\s)(${pat})(\\s|$)`, "i");
    const m = re.exec(t);
    if (m) {
      // Strip the matched scope phrase out so the residual text is
      // just the value tokens.
      const stripped = (t.slice(0, m.index) + " " + t.slice(m.index + m[0].length)).trim();
      return { scope, stripped };
    }
  }
  return { scope: null, stripped: t };
}

export function parseVoicePhrase(text, batterHand = "RIGHT", stickyScope = null, currentSpatial = null) {
  return parse(text, batterHand, stickyScope, currentSpatial);
}

function parse(text, batterHand = "RIGHT", stickyScope = null, currentSpatial = null) {
  // STT pre-clean: lowercase, strip trailing punctuation, apply
  // user-defined voice macros, then swap misheard tokens. Macros
  // run BEFORE alias normalisation so an operator's shortcut
  // expands first, then the expansion is cleaned for the parser.
  const expanded = applyMacros(text.toLowerCase().trim().replace(/[.,!?]+$/, ""));
  const raw = normalizeSttAliases(expanded);
  if (!raw) return null;

  // Top-priority action commands
  if (/^(save|save ball|score it|done|next ball)\b/.test(raw)) return { kind: "save" };
  // T141: "undo last 5" / "undo five" / "undo last five balls" — bulk
  // undo. Numeric capture first so it doesn't shadow the plain
  // "undo" command. Word-number support via _parseSpokenNumber.
  {
    const m = raw.match(/^undo\s+(?:last\s+)?(\d{1,2}|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|twenty\s*five)(?:\s+balls?)?\s*$/);
    if (m) {
      const n = _parseSpokenNumber(m[1].replace(/\s+/g, ""));
      if (n >= 2 && n <= 25) {
        return { kind: "undo-last-n", n };
      }
    }
  }
  if (/^(undo|undo ball|cancel last|take that back)\b/.test(raw)) return { kind: "undo" };
  if (/^(redo|redo ball|redo last)\b\s*$/.test(raw)) return { kind: "redo" };
  if (/^(cancel|nope|stop)\b/.test(raw)) return { kind: "cancel" };

  // ── Left-panel action buttons (Krishna's team 2026-05-26) ──────────
  // Operator-spoken commands for the toolbar actions that aren't
  // ball-field updates: clip in/out, DRS review, QC page, edit ball.
  // Each maps to a dedicated callback the SPA provides.
  //
  // 2026-05-28 — token-based clip-in / clip-out detection. The strict
  // regex kept missing in real-world Whisper output ("now clip in
  // please", "hey clip in here", "mark clip in", "tip in", "click
  // out"). New approach: tokenise the normalised utterance (splitting
  // on whitespace, hyphens, punctuation) and check whether it
  // contains a clip-verb token AND a direction token. Bounded by an
  // 8-token cap so long sentences ("click on the second link in the
  // menu") can't false-fire.
  {
    const CLIP_VERBS = new Set([
      "clip", "clips", "cliped", "clipped", "clipping",
      "click", "klick", "klip", "klipped", "klipping",
      "kleep", "kleeping", "cleep", "cleeping",
    ]);
    // "on" dropped — "click on link" was a false-positive risk. Whisper
    // mostly hears "in" correctly; the homophone problem is on the
    // VERB side ("clip" → "click" / "kleep"), not the direction word.
    // "tip" dropped from verbs for the same reason — too generic.
    const IN_WORDS  = new Set(["in", "inn", "into", "start", "begin"]);
    const OUT_WORDS = new Set(["out", "off", "end", "stop", "done", "finish", "finished"]);
    // Strip punctuation, split on whitespace + hyphens. Keep tokens
    // short and lowercased (raw is already lowercased above).
    const tokens = raw.replace(/[^a-z0-9\s-]/g, " ")
                      .split(/[\s-]+/)
                      .filter(Boolean);
    if (tokens.length > 0 && tokens.length <= 8) {
      const hasVerb = tokens.some((t) => CLIP_VERBS.has(t));
      if (hasVerb) {
        const hasIn  = tokens.some((t) => IN_WORDS.has(t));
        const hasOut = tokens.some((t) => OUT_WORDS.has(t));
        // If BOTH present (unlikely) prefer the LAST one — operator
        // probably corrected mid-utterance ("clip in out" → out).
        if (hasIn && hasOut) {
          const lastIn  = Math.max(...tokens.map((t, i) => IN_WORDS.has(t)  ? i : -1));
          const lastOut = Math.max(...tokens.map((t, i) => OUT_WORDS.has(t) ? i : -1));
          return { kind: lastIn > lastOut ? "clip-in" : "clip-out" };
        }
        if (hasIn)  return { kind: "clip-in" };
        if (hasOut) return { kind: "clip-out" };
      }
    }
  }
  // "start clip" / "begin clip" / "mark clip in" / "end clip" / "stop clip"
  // / "finish clip" — phrasings where the verb leads. The token-based
  // matcher above catches most of these via CLIP_VERBS, but the leading-
  // verb form ("start clip" — no in/out direction) still needs an
  // explicit rule to map to clip-in.
  if (/^(?:start|begin|mark|new)\s+clip(?:\s+in)?\s*$/.test(raw)) return { kind: "clip-in" };
  if (/^(?:end|stop|finish|done|complete)\s+clip(?:\s+out)?\s*$/.test(raw)) return { kind: "clip-out" };
  if (/^(drs|review|drs review|umpire review|call drs)\s*$/.test(raw)) return { kind: "open-drs" };
  if (/^(qc|quality check|go to qc|open qc)\s*$/.test(raw)) return { kind: "open-qc" };
  if (/^(edit ball|open edit|edit last ball)\s*$/.test(raw)) return { kind: "open-edit-ball" };

  // ── Cross-ball edit: "edit ball 2.4 to wide" / "change ball 3 over
  // 2 to four boundary". Founder ask 2026-05-26. Parses an over.ball
  // reference + a target phrase that gets re-parsed once the ball
  // dialog opens to apply the edit. Two forms:
  //   "edit ball <over>.<ball> to <phrase>"
  //   "change ball <over>.<ball> <phrase>"
  //   "change ball over <X> ball <Y> to <phrase>"
  {
    let m = raw.match(/^(?:edit|change|fix)\s+ball\s+(?:over\s+)?(\d+)\s*[.\s]\s*(?:ball\s+)?(\d+)\s+(?:to\s+)?(.+)$/);
    if (m) {
      const over = parseInt(m[1], 10);
      const ball = parseInt(m[2], 10);
      const inner = m[3].trim();
      if (over >= 1 && over <= 200 && ball >= 1 && ball <= 9 && inner.length >= 1) {
        return { kind: "edit-ball-by-ref", over, ball, inner };
      }
    }
  }

  // ── Undo a specific past ball — "undo ball 2.4" / "delete ball
  // over 3 ball 2". Different from plain "undo" (which always
  // removes the most recent ball). Founder ask 2026-05-26.
  {
    const m = raw.match(/^(?:undo|delete|remove|drop)\s+ball\s+(?:over\s+)?(\d+)\s*[.\s]\s*(?:ball\s+)?(\d+)\s*$/);
    if (m) {
      const over = parseInt(m[1], 10);
      const ball = parseInt(m[2], 10);
      if (over >= 1 && over <= 200 && ball >= 1 && ball <= 9) {
        return { kind: "undo-ball-by-ref", over, ball };
      }
    }
  }

  // ── Voice corrections ────────────────────────────────────────────
  // "no, I said FOUR" / "sorry, four" / "correction four boundary"
  // Operators retract a mis-recognised previous utterance and supply
  // the right phrase in one shot. Implementation: undo last applied
  // edit, then re-parse the inner phrase and apply normally.
  // Founder ask 2026-05-26.
  const corrMatch = raw.match(/^(?:no[, ]+|wrong[, ]+|sorry[, ]+|correction[, ]+|actually[, ]+)(?:i (?:meant|said|wanted)[, ]+)?(.+)$/);
  if (corrMatch && corrMatch[1].trim().length >= 2) {
    return { kind: "correction", inner: corrMatch[1].trim() };
  }

  // ── Explicit clear/reset (keeps bowler + batsmen, clears the rest)
  // Different from "save" — clears the IN-PROGRESS ball without
  // committing. Useful when the operator wants to start the ball
  // over without saving the half-built record.
  if (/^(clear|reset)( ball)?\s*$|^start over\s*$/.test(raw)) {
    return { kind: "clear-ball" };
  }

  // Wicket-modal open: bare "wicket" / "out" → ask the SPA to open the
  // dismissal picker. (Re-added 2026-05-26 per Krishna's reversed ask:
  // previously we silenced this because it false-fired on "out swinger";
  // now we require the word to STAND ALONE — not in a longer phrase
  // like "outside off" / "out swinger" / "shoulder arms ball goes
  // outside off". This is the explicit intent path.)
  if (/^(open )?(wicket|out|dismissal|how(s|'s)? that)\b\s*$/.test(raw)) {
    return { kind: "open-wicket-modal" };
  }

  // ── Explicit spatial navigation commands ─────────────────────────────
  // Founder ask 2026-05-26: "When I say pitch map I want you to toggle
  // to that, same for height impact, wagon wheel. Fix this!" The
  // scope-detection path already supported "pitch map" via
  // SCOPE_KEYWORDS, but that path was fragile — it depended on the
  // utterance being JUST the scope and nothing else, and any STT slip
  // ("pictures map" / "patch map") killed it. This direct command path
  // catches the navigation intent FIRST, with wider phrasing + STT
  // mishearing tolerance, before any scope / metric scanning runs.
  //
  // Accepted phrasings (case-insensitive, anchored to whole utterance):
  //   "pitch", "pitch map", "pitch zone", "pitching", "landing",
  //   "go to pitch", "show pitch", "open pitch", "switch to pitch",
  //   and the same family for "impact" / "height impact" / "height map"
  //   and "wagon" / "wagon wheel" / "field" / "field positions".
  //
  // Common STT mishearings handled inline (the alias dict already
  // covers many; these are direct-phrase only, so they don't bleed
  // into metric scans elsewhere): pictures/patch/stitch → pitch,
  // hight/hyte → height, wegan/wedding → wagon.
  const NAV_PREFIX = "(?:go to |goto |show |open |switch to |jump to |take me to )?";
  const NAV_PATTERNS = [
    { re: new RegExp(`^${NAV_PREFIX}(pitch( ?(map|zone))?|pitching|landed|landing|the pitch|pictures map|patch map|stitch map|hitch map|fish map|fitch map)$`, "i"),
      target: "pitch" },
    { re: new RegExp(`^${NAV_PREFIX}(height(\\s+(impact|map|zone))?|impact( ?(map|zone))?|hit at|hyte impact|hight impact|the impact|impact tab|height tab)$`, "i"),
      target: "impact" },
    { re: new RegExp(`^${NAV_PREFIX}(wagon(\\s+(wheel|map|zone))?|wagon|wegan|wedding|wedding wheel|the wagon|field positions?|fielding(\\s+map)?|wagon tab)$`, "i"),
      target: "wagon" },
  ];
  for (const { re, target } of NAV_PATTERNS) {
    if (re.test(raw)) {
      const label = target === "pitch"  ? "→ pitch map"
                  : target === "impact" ? "→ height impact"
                  : target === "wagon"  ? "→ wagon wheel"
                  : `→ ${target}`;
      return { kind: "navigate", target, label };
    }
  }

  // Match-state commands (player swaps, innings end, breaks, DLS, new ball)
  const state = parseStateCommand(raw);
  if (state) return state;

  // Scope detection. If a metric-name keyword is in the utterance,
  // narrow the per-field scans to ONLY that metric's map(s). This
  // eliminates cross-contamination ("stroke no shot" no longer
  // accidentally sets pitch_zone=Short via STT "no short", "wagon
  // cover" doesn't fire STROKE Cover Drive's "cover", etc).
  const { scope: detectedScope, stripped } = detectScope(raw);
  const t = stripped;
  // Sticky-scope fallback: after "pitch map" / "height impact" /
  // "wagon wheel" navigate commands, the SPA stays in that scope
  // for subsequent utterances until an explicit different scope OR
  // save/cancel/clear/undo. Founder report 2026-05-26: bare "42"
  // after "pitch map" was misparsing as runs=2 because Whisper
  // hears "forty two" and "two" matched RUN_WORDS.
  const scope = detectedScope || stickyScope || null;
  // When scope is set, build a whitelist of map IDs the field scans
  // should consult. Unrelated maps stay silent.
  const allow = scope ? new Set(SCOPE_TO_MAPS[scope] || []) : null;
  const canScan = (mapId) => !allow || allow.has(mapId);

  // Commentary scope short-circuit: everything after the keyword goes
  // straight into auto_commentary. No metric scans run; an empty body
  // is a no-op (the operator probably said "commentary" alone by
  // accident — don't blank the existing text).
  if (scope === "commentary") {
    if (!t) return { kind: "unrecognized", text: text };
    return {
      kind: "ball",
      updates: { auto_commentary: t },
      label: `commentary: ${t.length > 40 ? t.slice(0, 40) + "…" : t}`,
    };
  }

  // Scope-alone navigation: operator said just the section name with
  // nothing after ("height impact", "wagon wheel", "pitch map"). Hand
  // a navigate hint to the SPA so it switches the tab / expands the
  // accordion section. The operator's next utterance then sets the
  // value within the newly-focused section. (Krishna ask 2026-05-26.)
  if (scope && !t) {
    return { kind: "navigate", target: scope, label: `→ ${scope}` };
  }

  const updates = {};
  let interpreted = [];
  if (scope) interpreted.push(`[${scope}]`);

  // Wicket — only fire if a SPECIFIC wicket_type is matched. Bare
  // "wicket" / "out" used to also trigger has_wicket=true which then
  // auto-opened the wicket modal — false positives every time the
  // operator said "out swinger", "outside off", "shouldered arms,
  // ball went past the stumps", etc. (Krishna ask 2026-05-25.)
  // Now: explicit only. Operator can still tap-open the modal.
  let wicketType = null;
  for (const [pat, type] of WICKET_MAP) {
    if (new RegExp(`\\b(${pat})\\b`).test(t)) {
      wicketType = type;
      break;
    }
  }
  if (wicketType) {
    updates.has_wicket = true;
    updates.wicket_type = wicketType;
    interpreted.push(`wicket ${wicketType}`);
  }

  // Suppress runs / extras scans when the utterance is "JUST a number"
  // under a spatial scope — that's a cell-pick, not a run. Whisper
  // transcribes "42" as "forty two" → "two" used to match RUN_WORDS
  // and silently set runs=2. Founder report 2026-05-26.
  // Narrow heuristic: when scope is pitch/impact AND the utterance
  // is short and number-only, skip runs/extras. Allows "four boundary"
  // / "wide" to still fire when the operator says a full phrase.
  //
  // 2026-05-27 refinement (founder report "users have to repeat
  // runs"): if the spatial cell for the CURRENT scope is already set
  // (operator has already done their pitch / impact / wagon cell
  // pick for this ball), let bare numbers fall through to runs.
  // Without this the operator had to say "five runs" / "5 runs"
  // every time, even though "five" alone is the universal cricket
  // scoring shorthand.
  const isSpatialScope = scope === "pitch" || scope === "impact" || scope === "wagon";
  const cellAlreadySet =
    (scope === "pitch"  && !!currentSpatial?.pitch_zone)
    || (scope === "impact" && !!currentSpatial?.height_zone)
    || (scope === "wagon"  && !!currentSpatial?.structured_position);
  const isNumberOnlyUtterance = isSpatialScope && !cellAlreadySet && (
    /^(cell\s+|number\s+|no\s*\.?\s*)?(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty)(\s+(one|two|three|four|five|six|seven|eight|nine))?\s*$/.test(t)
  );

  // Extras
  if (!isNumberOnlyUtterance) {
    if (/\bwide\b/.test(t)) {
      updates.is_wide = true;
      interpreted.push("wide");
      // 2026-06-10 — Law 22 wide-subtype parse. Pairs with the
      // EditBallDialog wide_type picker that shipped in the same
      // batch. Canonical values match the Ball.WIDE_TYPE_CHOICES enum
      // on the backend (migration 0050).
      let wideType = null;
      if (/leg.?side|down.?leg|leg.?stump.?side/.test(t))             wideType = "LEGSIDE";
      else if (/above.?shoulder|over.?shoulder|head.?high/.test(t))   wideType = "ABOVE_SHOULDER";
      else if (/off.?side|outside.?off/.test(t))                       wideType = "OFFSIDE";
      if (wideType) { updates.wide_type = wideType; interpreted.push(`wide-type ${wideType}`); }
    }
    if (/no.?ball|no-ball/.test(t)) {
      updates.is_no_ball = true;
      interpreted.push("no-ball");
      // 2026-05-27: also parse the no_ball_type sub-selection so voice
      // can specify Overstep / Height (waist) / Beamer / Dangerous in
      // the same utterance. Canonical strings match the SPA's no_ball
      // type dropdown — keep in sync if those labels change.
      let nbType = null;
      if (/front.?foot|overstep/.test(t))                       nbType = "Overstep";
      else if (/waist.?high|height.?(waist|nb|no.?ball)|hip high/.test(t)) nbType = "Height (waist)";
      else if (/beamer|above.?shoulder|shoulder.?high/.test(t)) nbType = "Beamer (above shoulder)";
      else if (/dangerous/.test(t))                             nbType = "Dangerous (action)";
      if (nbType) { updates.no_ball_type = nbType; interpreted.push(`nb-type ${nbType}`); }
    }
    if (/leg.?bye|legbye/.test(t))           { updates.is_leg_bye = true; updates.is_bye = false; interpreted.push("leg-bye"); }
    else if (/\bbye(?!s? to)/.test(t))       { updates.is_bye = true; interpreted.push("bye"); }
    if (/dead.?ball|deadball/.test(t))       { updates.is_dead_ball = true; interpreted.push("dead-ball"); }
    if (/free.?hit|freehit/.test(t))         { updates.is_free_hit = true; interpreted.push("free-hit"); }

    // 2026-05-27 — appealed_for (chip group shown when wicket is
    // LBW/CAUGHT/STUMPED/RUN_OUT). "appeal lbw", "stumping appeal",
    // "appealed for caught", "caught behind appeal".
    {
      const ap = t.match(/(?:^|\s)(?:appeal(?:ed for)?\s+(lbw|caught(?:\s+behind)?|stumped|run.?out|stumping)|(\w+)\s+appeal)/);
      if (ap) {
        const raw = (ap[1] || ap[2] || "").toLowerCase().trim();
        const APPEAL_MAP = {
          "lbw": "LBW",
          "caught": "CAUGHT", "caught behind": "CAUGHT",
          "stumped": "STUMPED", "stumping": "STUMPED",
          "run out": "RUN_OUT", "runout": "RUN_OUT", "run-out": "RUN_OUT",
        };
        const ap2 = APPEAL_MAP[raw.replace(/[-]/g, " ")];
        if (ap2) { updates.appealed_for = ap2; interpreted.push(`appeal ${ap2}`); }
      }
    }

    // 2026-05-27 — run_out_subtype hints (Law 38). Set on top of the
    // wicket_type=RUN_OUT or MANKAD that the wicket scan / handler
    // produces. Standard is the default in the SPA dropdown so we
    // only need to surface the non-default subtypes via voice.
    if (/\bmankad\b/.test(t)) {
      updates.run_out_subtype = "MANKAD";
      interpreted.push("subtype MANKAD");
    } else if (/non.?striker(?:'s)?\s*end/.test(t)) {
      updates.run_out_subtype = "NON_STRIKER_END";
      interpreted.push("subtype non-striker end");
    } else if (/striker(?:'s)?\s*end|failed (run|to make|to ground)/.test(t)) {
      updates.run_out_subtype = "STRIKER_END";
      interpreted.push("subtype striker end");
    } else if (/both batters?\s*(?:out|out of crease|short)/.test(t)) {
      updates.run_out_subtype = "BOTH_BATTERS_OUT_OF_CREASE";
      interpreted.push("subtype both batters");
    } else if (/\binterference\b|\bobstruction\b/.test(t)) {
      updates.run_out_subtype = "INTERFERENCE";
      interpreted.push("subtype interference");
    }
    // Quick run / Short run (Krishna ask 2026-05-26). Long form OR the
    // 2-letter code. Tight regex on the code form so it doesn't fire
    // inside other words ("qr code" wouldn't false-match because
    // "qr" is preceded/followed by word boundaries).
    if (/\bquick run|quick running\b|^qr\s*$|\bqr\b/.test(t)) {
      updates.is_quick_running = true; interpreted.push("quick-run");
    }
    if (/\bshort run|short running\b|^sr\s*$|\bsr\b/.test(t)) {
      updates.is_short_run = true; interpreted.push("short-run");
    }

    // Runs — two-pass scan.
    //   Pass 1: idioms (dot / single / couple / boundary / sixer /
    //           maximum) fire UNCONDITIONALLY. They're unambiguous
    //           cricket terms that never mean anything else.
    //   Pass 2: numeric tokens (digits + spelled-out numbers) ONLY
    //           fire when the utterance contains the literal word
    //           "run" or "runs". Founder rule 2026-05-28: bare
    //           numbers won't set runs anymore — operator must say
    //           "X runs" / "X run" / "runs X" for a numeric runs
    //           change. This eliminates the cell-number / runs
    //           confusion across pitch / impact / wagon tabs.
    let matchedRun = false;
    for (const [pat, n] of RUN_IDIOM_WORDS) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        updates.completed_runs = n;
        if (n === 4 || n === 6) updates.is_boundary = true;
        interpreted.push(`${n} run${n === 1 ? "" : "s"}`);
        matchedRun = true;
        break;
      }
    }
    if (!matchedRun && /\brun(s)?\b/.test(t)) {
      for (const [pat, n] of RUN_NUMBER_WORDS) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          updates.completed_runs = n;
          if (n === 4 || n === 6) updates.is_boundary = true;
          interpreted.push(`${n} run${n === 1 ? "" : "s"}`);
          break;
        }
      }
    }
  }

  // Spatial — pitch length, stump line, height, wagon-wheel position.
  // Each loop is gated by canScan(MAP_ID) so a scoped utterance (e.g.
  // "wagon mid wicket") only scans the matching map.
  const spatial = {};
  // ── Cell-by-number shortcut ───────────────────────────────────────
  // Under scope=pitch OR scope=impact, a single number picks a cell.
  // Cells are numbered 1-56, left-to-right then top-to-bottom of the
  // RENDERED grid (so the operator just reads the number they see on
  // screen). Matrix is 8 rows × 7 cols.
  //   cell N → row_idx = floor((N-1)/7), col_idx = (N-1) % 7
  // For LH batters the visual cols are reversed (WDL on the left,
  // WOO on the right), so the canonical column code depends on
  // batterHand: LEFT mirrors col_idx → 6 - col_idx.
  // Founder ask 2026-05-26 (revision): "every cell gets a number,
  // they call out the number."
  if ((scope === "pitch" || scope === "impact") && !cellAlreadySet) {
    const cellMatch = t.match(/^(?:cell\s+|number\s+|no\s*\.?\s*)?(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty)(?:\s+(one|two|three|four|five|six|seven|eight|nine))?$/);
    if (cellMatch) {
      let n = _parseSpokenNumber(cellMatch[1]);
      const _TENS_BASE = { twenty: 20, thirty: 30, forty: 40, fifty: 50 };
      const baseTen = _TENS_BASE[cellMatch[1]?.toLowerCase()];
      if (baseTen && cellMatch[2]) n = baseTen + _parseSpokenNumber(cellMatch[2]);
      if (n >= 1 && n <= 56) {
        const rowIdx = Math.floor((n - 1) / 7);
        const visualColIdx = (n - 1) % 7;
        const canonicalColIdx = batterHand === "LEFT"
          ? (6 - visualColIdx)
          : visualColIdx;
        const colCode = COL_BY_NUMBER[canonicalColIdx];
        if (scope === "pitch") {
          const rowLabel = PITCH_ROW_BY_NUMBER[rowIdx];
          if (rowLabel) {
            spatial.pitch_zone = rowLabel;
            spatial.stump_zone = colCode;
            interpreted.push(`cell ${n} → ${rowLabel} / ${colCode}`);
          }
        } else if (scope === "impact") {
          const rowLabel = HEIGHT_ROW_BY_NUMBER[rowIdx];
          if (rowLabel) {
            spatial.height_zone = rowLabel;
            spatial.batter_stump_zone = colCode;
            interpreted.push(`cell ${n} → ${rowLabel} / ${colCode}`);
          }
        }
      }
    }
  }

  if (canScan("PITCH_LENGTH")) {
    for (const [pat, label] of PITCH_LENGTH_MAP) {
      // The bare "short" pattern is ambiguous when STT mishears "shot"
      // (stroke / connection) as "short" (pitch). Restrict it to
      // utterances that EITHER carry the "pitch" scope OR include a
      // pitch-context word ("ball", "pitched", "pitching", "length",
      // "back"). Compound matches like "short ball" / "short pitched"
      // remain free-form (they're in their own patterns earlier).
      // Founder report 2026-05-26 ("Shot connection directs to Short
      // option in pitch map").
      if (pat === "(?<!no )short") {
        const hasPitchContext = scope === "pitch"
          || /\b(pitch|pitched|pitching|length|back of|landed|delivery)\b/.test(t);
        if (!hasPitchContext) continue;
      }
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        spatial.pitch_zone = label;
        interpreted.push(`pitch ${label}`);
        break;
      }
    }
  }
  // Stump line routing: under "impact" scope it goes to batter_stump_zone;
  // under "pitch" scope it goes to stump_zone; with no scope (free-form),
  // the original behaviour applies — line → stump_zone first, then if
  // a height word also matched, mirrored to batter_stump_zone.
  let scannedLineCode = null;
  if (canScan("STUMP_LINE_PITCH") || canScan("STUMP_LINE_IMPACT")) {
    for (const [pat, code] of STUMP_LINE_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        scannedLineCode = code;
        break;
      }
    }
    // Loose fallback: under explicit "impact" or "pitch" scope, bare
    // directional words ("middle", "off", "leg", "down leg") count as
    // a line. Without this the natural shorthand "impact chest middle"
    // leaves batter_stump_zone unset, and the ZoneMatrixPicker's
    // isHeightSelected gate (heightZone && batterStumpZone) never
    // fires — cell stays unhighlighted (founder report 2026-05-27).
    if (!scannedLineCode && (scope === "impact" || scope === "pitch")) {
      for (const [pat, code] of STUMP_LINE_LOOSE_MAP) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          scannedLineCode = code;
          break;
        }
      }
    }
    if (scannedLineCode) {
      if (scope === "impact") {
        spatial.batter_stump_zone = scannedLineCode;
        interpreted.push(`impact line ${scannedLineCode}`);
      } else {
        spatial.stump_zone = scannedLineCode;
        interpreted.push(`line ${scannedLineCode}`);
      }
    }
  }

  // Height impact — second matrix. Under "impact" scope, accept bare
  // body-part words too (just "chest" without "high"). Otherwise the
  // map requires the natural qualifier ("chest high"). When height
  // fires WITHOUT scope, mirror the pitch-line into batter_stump_zone
  // so the impact cell highlights cleanly even when the operator
  // didn't preface with "impact".
  if (canScan("HEIGHT")) {
    for (const [pat, label] of HEIGHT_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        spatial.height_zone = label;
        interpreted.push(`impact ${label}`);
        break;
      }
    }
    // Loose scope-only fallback: under "impact" scope a bare body part
    // is enough.
    if (!spatial.height_zone && scope === "impact") {
      for (const [pat, label] of HEIGHT_LOOSE_MAP) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          spatial.height_zone = label;
          interpreted.push(`impact ${label}`);
          break;
        }
      }
    }
    if (spatial.height_zone && !scope && scannedLineCode && !spatial.batter_stump_zone) {
      // Free-form (no scope): a height match also propagates the line
      // to batter_stump_zone so "chest high outside off" highlights
      // the impact cell without needing the "impact" prefix.
      spatial.batter_stump_zone = scannedLineCode;
    }
  }

  // Wagon cell-by-number — under scope=wagon, a single number 1..16
  // picks the corresponding zone. 16 zones = 8 sectors × {ring, deep}
  // as numbered on the wheel: 1=sector 0 ring (Mid Off), 2=sector 0
  // deep (Long Off), … 15=sector 7 ring (Mid On), 16=sector 7 deep
  // (Long On). Bumped from 1..8 sector-only on 2026-05-27 — founder
  // ask for finer voice granularity matching the pitch/impact 1-56
  // scheme. Position label is resolved by resolvePosition() so it
  // respects batterHand (off/leg flip for LH).
  if (scope === "wagon" && !cellAlreadySet) {
    const cellMatch = t.match(/^(?:cell\s+|number\s+|no\s*\.?\s*|wagon\s+)?(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen)$/);
    if (cellMatch) {
      const n = _parseSpokenNumber(cellMatch[1]);
      if (n >= 1 && n <= 16) {
        const mapping = wagonCellNumberToSliceBand(n);
        if (mapping) {
          const position = resolvePosition(mapping.sliceIndex, mapping.bandIndex, batterHand);
          spatial.structured_region_id = `${mapping.sliceIndex}-${mapping.bandIndex}`;
          spatial.structured_slice_index = mapping.sliceIndex;
          spatial.structured_band_index = mapping.bandIndex;
          spatial.structured_position = position;
          interpreted.push(`wagon ${n} → ${position}`);
        }
      }
    }
  }

  if (canScan("WAGON_POSITION")) {
    for (const [pat, mapping] of WAGON_POSITION_MAP) {
      // "to mid wicket" / "played to cover" / just "mid wicket"
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        spatial.structured_region_id = `${mapping.slice}-${mapping.band}`;
        spatial.structured_slice_index = mapping.slice;
        spatial.structured_band_index = mapping.band;
        spatial.structured_position = mapping.position;
        interpreted.push(`to ${mapping.position}`);
        break;
      }
    }
  }
  if (Object.keys(spatial).length > 0) {
    updates.__spatial = spatial;
  }

  // Activity tracker fields — each gated by canScan() so a scoped
  // utterance only touches the matching field. Free-form (no scope)
  // utterances still scan every map (legacy behaviour preserved).
  const analytics = {};
  if (canScan("STROKE")) {
    for (const [pat, label] of STROKE_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.stroke = label;
        interpreted.push(`stroke ${label}`);
        break;
      }
    }
  }
  if (canScan("FOOT")) {
    for (const [pat, label] of FOOT_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.foot_movement = label;
        interpreted.push(`foot ${label}`);
        break;
      }
    }
  }
  if (canScan("CONNECTION")) {
    for (const [pat, label] of CONNECTION_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.shot_connection = label;
        interpreted.push(`connection ${label}`);
        break;
      }
    }
  }
  // Bowler plan vocab — UI field removed; left for any legacy caller.
  for (const [pat, label] of BOWLER_PLAN_MAP) {
    if (!scope && new RegExp(`\\b(${pat})\\b`).test(t)) {
      analytics.bowler_plan = label;
      interpreted.push(`plan ${label}`);
      break;
    }
  }
  // Performance-metric chip groups (Krishna ask 2026-05-25). Each map
  // sets its dedicated analytics field; independent loops so a single
  // utterance can populate many fields at once.
  if (canScan("DELIVERY_TYPE")) {
    for (const [pat, label] of DELIVERY_TYPE_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.delivery_type = label;
        interpreted.push(`delivery ${label}`);
        break;
      }
    }
  }
  if (canScan("SPIN_DIRECTION")) {
    for (const [pat, label] of SPIN_DIRECTION_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.spin_direction = label;
        interpreted.push(`spin ${label}`);
        break;
      }
    }
    if (!analytics.spin_direction && scope === "spin") {
      for (const [pat, label] of SPIN_DIRECTION_LOOSE_MAP) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          analytics.spin_direction = label;
          interpreted.push(`spin ${label}`);
          break;
        }
      }
    }
  }
  if (canScan("DRIFT")) {
    for (const [pat, label] of DRIFT_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.drift_direction = label;
        interpreted.push(`drift ${label}`);
        break;
      }
    }
    if (!analytics.drift_direction && scope === "drift") {
      for (const [pat, label] of DRIFT_LOOSE_MAP) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          analytics.drift_direction = label;
          interpreted.push(`drift ${label}`);
          break;
        }
      }
    }
  }
  if (canScan("TURN")) {
    for (const [pat, label] of TURN_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.turn_after_pitch = label;
        interpreted.push(`turn ${label}`);
        break;
      }
    }
    if (!analytics.turn_after_pitch && scope === "turn") {
      for (const [pat, label] of TURN_LOOSE_MAP) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          analytics.turn_after_pitch = label;
          interpreted.push(`turn ${label}`);
          break;
        }
      }
    }
  }
  if (canScan("KEEPER")) {
    for (const [pat, label] of KEEPER_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.keeper_activity = label;
        interpreted.push(`keeper ${label}`);
        break;
      }
    }
    if (!analytics.keeper_activity && scope === "keeper") {
      for (const [pat, label] of KEEPER_LOOSE_MAP) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          analytics.keeper_activity = label;
          interpreted.push(`keeper ${label}`);
          break;
        }
      }
    }
  }
  if (canScan("BOUNCE")) {
    for (const [pat, label] of BOUNCE_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.bounce_category = label;
        interpreted.push(`bounce ${label}`);
        break;
      }
    }
    if (!analytics.bounce_category && scope === "bounce") {
      for (const [pat, label] of BOUNCE_LOOSE_MAP) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          analytics.bounce_category = label;
          interpreted.push(`bounce ${label}`);
          break;
        }
      }
    }
  }
  if (canScan("AIR_MOVEMENT")) {
    for (const [pat, label] of AIR_MOVEMENT_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.air_movement = label;
        interpreted.push(`air ${label}`);
        break;
      }
    }
    if (!analytics.air_movement && scope === "air") {
      for (const [pat, label] of AIR_LOOSE_MAP) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          analytics.air_movement = label;
          interpreted.push(`air ${label}`);
          break;
        }
      }
    }
  }
  if (canScan("CONTROL")) {
    for (const [pat, label] of CONTROL_MAP) {
      if (new RegExp(`\\b(${pat})\\b`).test(t)) {
        analytics.control = label;
        interpreted.push(`control ${label}`);
        break;
      }
    }
    if (!analytics.control && scope === "control") {
      for (const [pat, label] of CONTROL_LOOSE_MAP) {
        if (new RegExp(`\\b(${pat})\\b`).test(t)) {
          analytics.control = label;
          interpreted.push(`control ${label}`);
          break;
        }
      }
    }
  }
  if (Object.keys(analytics).length > 0) {
    updates.__analytics = analytics;
  }

  if (Object.keys(updates).length > 0) {
    return { kind: "ball", updates, label: interpreted.join(" + ") || "—" };
  }
  return { kind: "unrecognized", text: t };
}

export function useVoiceScoring({
  // 2026-05-27 — sync voice sticky-scope with the visible spatial tab.
  // Without this prop, the parser's stickyScopeRef only updated via
  // explicit "pitch map" / "wagon wheel" voice navigations, so the
  // auto-tab-advance (pitch → impact → wagon as the operator codes)
  // left voice still scoped to the previous tab. Result: operator looks
  // at the wagon tab, says "5", parser uses scope="pitch" (stale), sets
  // pitch cell 5 instead of wagon cell 5. Now BBB passes the live tab
  // and the hook mirrors it into the scope ref.
  activeSpatialTab,   // "pitch" | "impact" | "wagon" | null
  currentSpatial,     // ball.spatial snapshot — used to decide whether
                      //   bare-number utterances fill a cell or fall
                      //   through to runs. Read once per utterance
                      //   via a ref so React state churn doesn't break
                      //   the parser closure.
  // 2026-05-28 — between-overs / wicket-fall player picker context.
  // When `open` is true the hook will treat any otherwise-unrecognised
  // utterance as a possible player-name pick, fuzzy-match against
  // `candidates`, and dispatch via onModalNamePick(playerId). That
  // lets the operator say just "arun lion" (no need to prefix
  // "bowler" / "striker") when the picker modal is showing.
  selectionModalContext = { open: false, type: null, candidates: [] },
  onModalNamePick,    // (playerId: string) => void
  setBall,
  onSave,
  onUndo,
  onCancel,
  // Match-state handlers — all optional. Missing handlers fall through to a
  // log-only "unhandled" lastApplied entry so the operator sees their voice
  // command was understood but the page can't act on it.
  onSetBowler,
  onSetStriker,
  onSetNonStriker,
  onSetKeeper,
  // 2026-05-27 wicket-fielder voice handlers — each receives the
  // spoken name(s) and resolves to player IDs via matchPlayer in BBP.
  // The handler also sets the matching wicket_type if the operator
  // skipped the explicit "wicket caught" / "wicket stumped" / etc.
  onSetCaughtBy,
  onSetStumpedBy,
  onSetRunOutFielders,    // (names: [string, string?])
  onSetMankadBy,
  onInningsEnd,
  onStartBreak,
  onDLS,
  onNewBall,
  // New 2026-05-26 (Krishna asks):
  // - onOpenWicketModal: bare "wicket" / "out" command opens dismissal picker
  // - onNavigate(target): scope keyword spoken alone ("height impact",
  //   "wagon wheel") tells the SPA to switch tab / expand section
  onOpenWicketModal,
  onNavigate,
  // Founder report 2026-05-26 — left-panel action buttons via voice.
  onRedo,          // ↻ redo last undone ball
  onClipIn,        // ▶ mark video clip start
  onClipOut,       // ■ mark video clip end
  onOpenDrs,       // → DRS / umpire review
  onOpenQc,        // → navigate to per-ball QC page
  onOpenEditBall,  // → open the EditBallDialog
  onEditBallByRef, // (over, ball, phrase) → open + edit a past ball
  onUndoBallByRef, // (over, ball) → undo a specific past ball
  enabled = true,
  // AI fallback: when true AND the regex parser returns kind:unrecognized,
  // POST the transcript to /api/scoring/ai/voice-parse/ and apply the
  // structured response. Bowler-type hint helps Claude pick the right
  // delivery-type pool. Charges 1 ZC per call via the backend mixin.
  aiFallbackEnabled = false,
  bowlerType = null,        // "PACE" | "SPIN" | null
  // Striker handedness — drives the cell-number → canonical column
  // mapping. LH batter sees the columns mirrored, so cell 1 is at
  // WDL not WOO. Default RIGHT.
  batterHand = "RIGHT",
}) {
  const recRef = useRef(null);
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [lastApplied, setLastApplied] = useState(null);
  // Ring buffer of the last 5 applied utterances, for the "recent
  // commands" strip the operator scans to spot mishears. Pushed via
  // a watch on lastApplied so we don't need to instrument every
  // setLastApplied call site.
  const [recentApplied, setRecentApplied] = useState([]);
  useEffect(() => {
    if (!lastApplied) return;
    setRecentApplied((prev) => {
      // Dedup back-to-back identical labels (interim parser fires
      // can otherwise spam the strip with the same text).
      if (prev[0] && prev[0].label === lastApplied.label && prev[0].ts === lastApplied.ts) {
        return prev;
      }
      return [lastApplied, ...prev].slice(0, 5);
    });
  }, [lastApplied]);
  const [error, setError] = useState("");
  // Auto-confirm queue for AI-fallback results (2-second hold before
  // apply, gives operator a chance to veto a Claude misparse). Regex
  // matches still apply instantly — only the AI path uses this gate.
  const [pendingApply, setPendingApply] = useState(null);
  const pendingTimerRef = useRef(null);

  // Ref to the apply-from-text function. The onresult handler below
  // populates this ref; external callers (the new Whisper push-to-talk
  // button) read it to dispatch a transcript through the SAME parse +
  // apply + AI-fallback path the browser-STT route uses. Keeps "what
  // does an utterance do" defined exactly once.
  const applyTranscriptRef = useRef(null);
  // Sticky scope — set by a "navigate" command ("pitch map", "height
  // impact", "wagon wheel") and consumed by the parser on the NEXT
  // utterances so bare numbers / option words resolve to the right
  // matrix. Cleared on save / undo / cancel / clear-ball or when
  // navigate switches to a different scope.
  const stickyScopeRef = useRef(null);
  // 2026-05-27: mirror the visible spatial tab into stickyScopeRef so
  // a tab auto-advance (pitch → impact → wagon) keeps the voice scope
  // aligned with what the operator sees. Without this, "5" on the
  // wagon tab was still being routed to pitch cell 5 because sticky
  // scope was set the last time the operator voiced "pitch map".
  useEffect(() => {
    if (activeSpatialTab === "pitch"
        || activeSpatialTab === "impact"
        || activeSpatialTab === "wagon") {
      stickyScopeRef.current = activeSpatialTab;
    }
  }, [activeSpatialTab]);

  // 2026-05-27 — mirror the in-progress ball's spatial state so the
  // parser can decide whether bare-number utterances should fill a
  // cell (cell unset) or fall through to runs (cell already set).
  // Without this the operator had to say "five runs" every time after
  // setting a cell, which they reported as having to "repeat runs".
  const currentSpatialRef = useRef(null);
  useEffect(() => { currentSpatialRef.current = currentSpatial || null; }, [currentSpatial]);

  // 2026-05-28 — Mirror activeSpatialTab into a ref so the save / undo /
  // cancel / clear handlers (which run inside applyParsed's closure)
  // can restore stickyScope from the *current* visible tab. Previously
  // those handlers nulled sticky on every save; the activeSpatialTab
  // effect only re-mirrors on CHANGE, so if the operator stayed on the
  // same pitch tab across balls, sticky stayed null and the next bare
  // "3" routed to runs instead of pitch cell 3. Founder report 2026-
  // 05-28: "if I select runs as 4 and then go to pitch map and select
  // 3, the runs is also changing to 3 — voice assistants should not
  // affect one another."
  const activeSpatialTabRef = useRef(null);
  useEffect(() => { activeSpatialTabRef.current = activeSpatialTab || null; }, [activeSpatialTab]);
  // 2026-05-28 — mirror the player-picker modal context so applyParsed
  // can intercept unrecognised utterances and try a fuzzy player match
  // (the operator just says "arun lion" while the picker is open).
  const selectionModalContextRef = useRef({ open: false, type: null, candidates: [] });
  useEffect(() => {
    selectionModalContextRef.current = selectionModalContext || { open: false, type: null, candidates: [] };
  }, [selectionModalContext]);
  // Restore (not null) sticky scope on save/undo/cancel/clear. If the
  // operator is on a spatial tab, keep that as the sticky scope so the
  // next utterance still routes correctly.
  const resetStickyScope = () => {
    const tab = activeSpatialTabRef.current;
    stickyScopeRef.current =
      (tab === "pitch" || tab === "impact" || tab === "wagon") ? tab : null;
  };
  // Ref to the applyParsed function (parsed + sourceText -> apply).
  // Same pattern but for the early-commit Apply button on the AI
  // suggestion toast — needs the full apply path, not a stripped one.
  const applyParsedRef = useRef(null);

  // Setup
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      setError("Voice scoring not supported in this browser. Use Chrome / Edge.");
      return;
    }
    setSupported(true);
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-IN";
    r.maxAlternatives = 1;
    recRef.current = r;
    return () => {
      try { r.abort(); } catch {}
    };
  }, []);

  const handlers = useRef({});
  useEffect(() => {
    handlers.current = {
      setBall, onSave, onUndo, onCancel,
      onSetBowler, onSetStriker, onSetNonStriker, onSetKeeper,
      onSetCaughtBy, onSetStumpedBy, onSetRunOutFielders, onSetMankadBy,
      onInningsEnd, onStartBreak, onDLS, onNewBall,
      onOpenWicketModal, onNavigate,
      onRedo, onClipIn, onClipOut, onOpenDrs, onOpenQc, onOpenEditBall, onEditBallByRef, onUndoBallByRef,
    };
  }, [
    setBall, onSave, onUndo, onCancel,
    onSetBowler, onSetStriker, onSetNonStriker, onSetKeeper,
    onSetCaughtBy, onSetStumpedBy, onSetRunOutFielders, onSetMankadBy,
    onInningsEnd, onStartBreak, onDLS, onNewBall,
    onOpenWicketModal, onNavigate,
    onRedo, onClipIn, onClipOut, onOpenDrs, onOpenQc, onOpenEditBall,
  ]);

  // Track what we've already applied this utterance, so interim
  // parsing + final parsing don't fire the same command twice. Reset
  // whenever a new utterance starts (interim goes empty after a final).
  const lastAppliedTextRef = useRef("");
  const interimTimerRef = useRef(null);
  // Per-utterance: track which "ball" sub-fields we've already fired so
  // later interims that add MORE content don't re-fire the earlier set.
  const firedBallFieldsRef = useRef(new Set());

  useEffect(() => {
    const r = recRef.current;
    if (!r) return;

    // Single apply function — called from interim (fast path) and from
    // final (catches anything the interim missed). Idempotent on the
    // command text via lastAppliedTextRef.
    const applyParsed = (parsed, sourceText) => {
      if (!parsed) return false;
      if (parsed.kind === "unrecognized") {
        // 2026-05-28 — when the picker modal (bowler / striker /
        // non-striker) is open, give an otherwise-unrecognised
        // utterance a second chance as a bare player name. Operator
        // says "arun lion" while the bowler picker is showing → we
        // fuzzy-match against the relevant candidate list and dispatch
        // via onModalNamePick which closes the modal + sets the right
        // ball field.
        const ctx = selectionModalContextRef.current;
        if (
          ctx?.open &&
          ctx.candidates?.length &&
          typeof handlers.current?.onModalNamePick === "function" &&
          sourceText && sourceText.trim().length >= 2
        ) {
          const p = matchPlayer(sourceText, ctx.candidates);
          if (p) {
            handlers.current.onModalNamePick(p.id);
            setLastApplied({ ts: Date.now(), label: `🎙 ${ctx.type} → ${p.name || p.short_name || p.id}` });
            lastAppliedTextRef.current = sourceText || "";
            return true;
          }
        }
        return false;
      }
      // Suppress exact-text re-fire (same utterance landing twice).
      if (sourceText && sourceText === lastAppliedTextRef.current) return false;

      const h = handlers.current;
      switch (parsed.kind) {
        case "save":
          resetStickyScope();
          h.onSave?.();
          setLastApplied({ ts: Date.now(), label: "↩ SAVE" });
          break;
        case "undo":
          resetStickyScope();
          h.onUndo?.();
          setLastApplied({ ts: Date.now(), label: "↺ UNDO" });
          break;
        case "undo-last-n":
          // T141: bulk undo from voice. h.onUndoLastN handles the
          // preview + confirm flow on the SPA side.
          resetStickyScope();
          h.onUndoLastN?.(parsed.n);
          setLastApplied({ ts: Date.now(), label: `↺ UNDO LAST ${parsed.n}` });
          break;
        case "cancel":
          resetStickyScope();
          h.onCancel?.();
          setLastApplied({ ts: Date.now(), label: "✕ CANCEL" });
          break;
        case "redo":
          h.onRedo?.();
          setLastApplied({ ts: Date.now(), label: "↻ REDO" });
          break;
        case "clip-in":
          h.onClipIn?.();
          setLastApplied({ ts: Date.now(), label: "▶ clip in" });
          break;
        case "clip-out":
          h.onClipOut?.();
          setLastApplied({ ts: Date.now(), label: "■ clip out" });
          break;
        case "open-drs":
          h.onOpenDrs?.();
          setLastApplied({ ts: Date.now(), label: "→ DRS" });
          break;
        case "open-qc":
          h.onOpenQc?.();
          setLastApplied({ ts: Date.now(), label: "→ QC" });
          break;
        case "open-edit-ball":
          h.onOpenEditBall?.();
          setLastApplied({ ts: Date.now(), label: "→ edit ball" });
          break;
        case "edit-ball-by-ref":
          h.onEditBallByRef?.(parsed.over, parsed.ball, parsed.inner);
          setLastApplied({ ts: Date.now(), label: `edit ${parsed.over}.${parsed.ball} → ${parsed.inner}` });
          break;
        case "undo-ball-by-ref":
          h.onUndoBallByRef?.(parsed.over, parsed.ball);
          setLastApplied({ ts: Date.now(), label: `undo ${parsed.over}.${parsed.ball}` });
          break;
        case "correction": {
          // Roll back the last applied edit + re-apply the inner phrase.
          h.onUndo?.();
          const innerParsed = parse(parsed.inner, batterHand, stickyScopeRef.current, currentSpatialRef.current);
          if (innerParsed && innerParsed.kind !== "unrecognized") {
            applyParsed(innerParsed, parsed.inner);
          }
          setLastApplied({ ts: Date.now(), label: `↺ corrected: ${parsed.inner.slice(0, 30)}` });
          break;
        }
        case "clear-ball": {
          // Reset the in-progress ball spatial + analytics + runs
          // WITHOUT saving. Keeps bowler / striker / non-striker IDs
          // because operators rarely want those wiped mid-over.
          resetStickyScope();
          h.setBall?.((prev) => ({
            bowler_id: prev.bowler_id,
            striker_id: prev.striker_id,
            non_striker_id: prev.non_striker_id,
            over_number: prev.over_number,
            ball_in_over: prev.ball_in_over,
            spatial: {},
            analytics: {},
          }));
          setLastApplied({ ts: Date.now(), label: "✕ ball cleared (kept bowler + batsmen)" });
          break;
        }
        case "set-bowler":
          h.onSetBowler?.(parsed.name);
          setLastApplied({ ts: Date.now(), label: `bowler → ${parsed.name}` });
          break;
        case "set-striker":
          h.onSetStriker?.(parsed.name);
          setLastApplied({ ts: Date.now(), label: `striker → ${parsed.name}` });
          break;
        case "set-non-striker":
          h.onSetNonStriker?.(parsed.name);
          setLastApplied({ ts: Date.now(), label: `non-striker → ${parsed.name}` });
          break;
        case "set-keeper":
          h.onSetKeeper?.(parsed.name);
          setLastApplied({ ts: Date.now(), label: `keeper → ${parsed.name}` });
          break;
        case "set-caught-by":
          h.onSetCaughtBy?.(parsed.name);
          setLastApplied({ ts: Date.now(), label: `caught by → ${parsed.name}` });
          break;
        case "set-stumped-by":
          h.onSetStumpedBy?.(parsed.name);
          setLastApplied({ ts: Date.now(), label: `stumped by → ${parsed.name}` });
          break;
        case "set-run-out-fielders":
          h.onSetRunOutFielders?.(parsed.names);
          setLastApplied({ ts: Date.now(), label: `run out → ${parsed.names.join(" + ")}` });
          break;
        case "set-mankad-by":
          h.onSetMankadBy?.(parsed.name);
          setLastApplied({ ts: Date.now(), label: `mankad by → ${parsed.name}` });
          break;
        case "innings-end":
          h.onInningsEnd?.(parsed.closure_type);
          setLastApplied({ ts: Date.now(), label: `innings end${parsed.closure_type ? ` (${parsed.closure_type})` : ""}` });
          break;
        case "start-break":
          h.onStartBreak?.(parsed.break_type);
          setLastApplied({ ts: Date.now(), label: `break: ${parsed.break_type}` });
          break;
        case "dls":
          // null target / overs = "open DLS panel without pre-fill"
          h.onDLS?.(parsed.target, parsed.overs);
          setLastApplied({
            ts: Date.now(),
            label: (parsed.target != null && parsed.overs != null)
              ? `DLS ${parsed.target}/${parsed.overs}`
              : "DLS panel opened",
          });
          break;
        case "new-ball":
          h.onNewBall?.();
          setLastApplied({ ts: Date.now(), label: "new ball taken" });
          break;
        // ── More-menu coverage (2026-06-11) ─────────────────────────
        case "end-match":
          h.onEndMatch?.();
          setLastApplied({ ts: Date.now(), label: "end match" });
          break;
        case "end-over-now":
          h.onEndOverNow?.();
          setLastApplied({ ts: Date.now(), label: "end this over now (phantom)" });
          break;
        case "add-phantom-ball":
          h.onAddPhantomBall?.();
          setLastApplied({ ts: Date.now(), label: "add phantom ball" });
          break;
        case "floodlights":
          h.onFloodlights?.(parsed.state);
          setLastApplied({
            ts: Date.now(),
            label: parsed.state === true ? "floodlights on"
                 : parsed.state === false ? "floodlights off"
                 : "floodlights toggle",
          });
          break;
        case "pause-match":
          h.onPauseMatch?.();
          setLastApplied({ ts: Date.now(), label: "match paused" });
          break;
        case "resume-match":
          h.onResumeMatch?.();
          setLastApplied({ ts: Date.now(), label: "match resumed" });
          break;
        case "toggle-pause":
          h.onTogglePause?.();
          setLastApplied({ ts: Date.now(), label: "toggle pause" });
          break;
        case "rain-affected":
          h.onRainAffected?.(parsed.state);
          setLastApplied({
            ts: Date.now(),
            label: parsed.state ? "rain affected on (DLS unlocked)" : "rain affected off",
          });
          break;
        case "rain-affected-start":
          h.onRainAffectedAtStart?.();
          setLastApplied({ ts: Date.now(), label: "rain affected at start — overs modal" });
          break;
        case "end-innings-rain":
          h.onEndInningsRainAffected?.();
          setLastApplied({ ts: Date.now(), label: "end innings — rain affected" });
          break;
        case "open-overs-remaining":
          h.onOpenOversRemaining?.();
          setLastApplied({ ts: Date.now(), label: "overs remaining modal" });
          break;
        case "open-impact":
          h.onOpenImpact?.();
          setLastApplied({ ts: Date.now(), label: "impact player modal" });
          break;
        case "open-concussion":
          h.onOpenConcussion?.();
          setLastApplied({ ts: Date.now(), label: "concussion sub modal" });
          break;
        case "test-stumps":
          h.onTestStumps?.();
          setLastApplied({ ts: Date.now(), label: "stumps — advance Test day" });
          break;
        case "test-lunch":
          h.onTestLunch?.();
          setLastApplied({ ts: Date.now(), label: "lunch — advance Test session" });
          break;
        case "test-tea":
          h.onTestTea?.();
          setLastApplied({ ts: Date.now(), label: "tea — advance Test session" });
          break;
        case "open-match-settings":
          h.onOpenMatchSettings?.();
          setLastApplied({ ts: Date.now(), label: "match settings modal" });
          break;
        case "open-fielding-penalty":
          h.onOpenFieldingPenalty?.();
          setLastApplied({ ts: Date.now(), label: "fielding penalty modal" });
          break;
        case "open-wicket-modal":
          h.onOpenWicketModal?.();
          setLastApplied({ ts: Date.now(), label: "open wicket picker" });
          break;
        case "navigate":
          // Sticky-scope set: subsequent utterances inherit this scope
          // until save/undo/cancel/clear-ball or a different navigate.
          // Founder fix 2026-05-26 — "42 after pitch map should set
          // cell 42, not runs=2".
          stickyScopeRef.current = parsed.target;
          h.onNavigate?.(parsed.target);
          setLastApplied({ ts: Date.now(), label: parsed.label || `→ ${parsed.target}` });
          break;
        case "ball": {
          // For ball updates we de-dupe per-field across the utterance:
          // interim "yorker" sets pitch_zone; later interim "yorker
          // outside off" adds stump_zone. We don't want pitch_zone to
          // be re-set (no-op anyway, but keeps the toast clean) — and
          // more importantly we want to AVOID re-firing analytics/
          // spatial that already landed.
          //
          // The set-once rule (2026-05-27 first ask) was reverted same
          // day per founder follow-up: voice CAN now overwrite an
          // already-set metric. The firedBallFieldsRef dedup still
          // resets per-utterance, so the per-utterance idempotency
          // for interim STT is preserved, but between separate
          // utterances ("cover drive" then "on drive") voice freely
          // overwrites.
          const { __spatial, __analytics, ...rest } = parsed.updates || {};
          const newSpatial = {};
          const newAnalytics = {};
          const newRest = {};
          const fired = firedBallFieldsRef.current;
          for (const [k, v] of Object.entries(__spatial || {})) {
            const key = `s:${k}`;
            if (!fired.has(key)) { newSpatial[k] = v; fired.add(key); }
          }
          for (const [k, v] of Object.entries(__analytics || {})) {
            const key = `a:${k}`;
            if (fired.has(key)) continue;
            // 2026-05-28 — coerce against the chip-group option list so
            // misheard values ("car drive") map to canonical labels
            // ("Cover Drive") and unrelated noise is rejected instead
            // of silently writing a string that no chip can highlight.
            const validOpts = FIELD_OPTIONS_MAP[k];
            let coerced = v;
            if (validOpts && validOpts.length && typeof v === "string") {
              const match = closestValidOption(v, validOpts);
              if (match == null) {
                // No close match — drop the field. Surface to the
                // operator so they know the utterance wasn't routed.
                setLastApplied({ ts: Date.now(), label: `⚠ "${v}" — no matching ${k.replace(/_/g, " ")} option` });
                continue;
              }
              coerced = match;
            }
            newAnalytics[k] = coerced;
            fired.add(key);
          }
          for (const [k, v] of Object.entries(rest)) {
            const key = `r:${k}`;
            if (!fired.has(key)) { newRest[k] = v; fired.add(key); }
          }
          // Nothing new this round — quiet no-op (don't toast).
          if (
            Object.keys(newSpatial).length === 0 &&
            Object.keys(newAnalytics).length === 0 &&
            Object.keys(newRest).length === 0
          ) {
            return false;
          }
          h.setBall?.((prev) => ({
            ...prev,
            ...newRest,
            spatial:   { ...(prev.spatial   || {}), ...newSpatial   },
            analytics: { ...(prev.analytics || {}), ...newAnalytics },
          }));
          setLastApplied({ ts: Date.now(), label: parsed.label });
          break;
        }
        default:
          setLastApplied({ ts: Date.now(), label: `unknown: "${parsed.text}"` });
          return false;
      }
      lastAppliedTextRef.current = sourceText || "";
      return true;
    };

    // Expose applyParsed so the Apply button on the AI-suggestion
    // toast can fire the SAME code path as auto-confirm. Without
    // this, confirmPending was using a stripped-down inline applier
    // that missed the spatial/region sync side-effects, so tapping
    // Apply did less than the 2-second auto-fire. Founder report
    // 2026-05-26 ("Apply has no selection").
    applyParsedRef.current = applyParsed;

    // Expose a stable entry point for external callers. Whisper PTT
    // hands transcripts in via this ref — same parse + applyParsed +
    // AI-fallback chain the browser-STT route uses.
    applyTranscriptRef.current = (text) => {
      const t = (text || "").trim();
      if (!t) return;
      setTranscript(t);
      const parsed = parse(t, batterHand, stickyScopeRef.current, currentSpatialRef.current);
      const handled = applyParsed(parsed, t);
      if (!handled && parsed && parsed.kind === "unrecognized" && aiFallbackEnabled) {
        const { scope: hintedScope } = detectScope(t.toLowerCase());
        (async () => {
          try {
            const api = (await import("../api/axios")).default;
            const { data } = await api.post("/api/scoring/ai/voice-parse/", {
              transcript: t,
              scope: hintedScope || null,
              bowler_type: bowlerType || null,
            });
            if (!data || data.kind === "unrecognized") return;
            // ── Confidence branching ───────────────────────────────
            // 2026-05-28 — founder lowered auto-apply threshold from
            // 0.9 to 0.75 ("if accuracy > 74% don't ask, just select").
            // 2026-06-10 voice audit — lowered ignore threshold from
            //   0.60 → 0.55 because Haiku regularly self-rates plausible
            //   parses at 0.55-0.60 (e.g. "yorker" alone) and the old
            //   threshold silently dropped them. Also bumped the
            //   default-when-missing from 0.75 → 0.85 so a missing
            //   confidence from the backend doesn't randomly land below
            //   the auto-apply line.
            // <0.55:   Claude isn't sure — silently ignore. Stops
            //          noisy mis-suggestions on every filler word.
            // 0.55-0.74: 5-second confirmation toast with Apply/Cancel.
            // >0.74:   auto-apply immediately.
            const conf = typeof data.confidence === "number" ? data.confidence : 0.85;
            if (conf < 0.55) {
              setLastApplied({ ts: Date.now(), label: `🤖 low confidence (${conf.toFixed(2)}) — ignored` });
              return;
            }
            if (conf > 0.74) {
              applyParsed(data, t);
              setLastApplied({ ts: Date.now(), label: `🤖 ${data.label || "AI parsed"} (${conf.toFixed(2)})` });
              return;
            }
            if (pendingTimerRef.current) {
              clearTimeout(pendingTimerRef.current);
              const prev = pendingApply;
              if (prev) applyParsed(prev.parsed, prev.sourceText);
              pendingTimerRef.current = null;
            }
            // 5 sec gives the operator time to read the suggestion +
            // tap Apply/Cancel deliberately. Was 2s; founder report
            // 2026-05-26 ("time for the suggestion should be extended").
            const COUNTDOWN_MS = 5000;
            const expiresAt = Date.now() + COUNTDOWN_MS;
            setPendingApply({ parsed: data, sourceText: t, label: data.label || "AI parsed", expiresAt, confidence: conf });
            pendingTimerRef.current = setTimeout(() => {
              applyParsed(data, t);
              setPendingApply(null);
              pendingTimerRef.current = null;
            }, COUNTDOWN_MS);
          } catch (e) {
            console.warn("voice AI fallback failed (PTT):", e?.response?.data || e);
          }
        })();
      }
      lastAppliedTextRef.current = "";
      firedBallFieldsRef.current = new Set();
    };

    r.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finalText += res[0].transcript;
        else interimText += res[0].transcript;
      }
      setTranscript((interimText || finalText).trim());

      // ── FAST PATH: act on interim ─────────────────────────────────
      // Web Speech API typically waits ~1s of silence before firing
      // `isFinal`. That latency frustrates rapid voice coding — Krishna
      // can rattle off three balls in the time the API decides "ok
      // they're done". We process interim too, with two layers of safety:
      //  1. ACTION commands ("save", "undo", "cancel") fire IMMEDIATELY
      //     on interim — they're short, unambiguous, time-sensitive.
      //  2. Everything else fires after a 400ms debounce on the
      //     interim text — gives STT a chance to refine ("yorker out…"
      //     → "yorker outside off") without re-firing.
      if (interimText && !finalText) {
        const trimmedInterim = interimText.trim();
        // Try fast-action match BEFORE the debounce — no point waiting
        // 400ms to send a "save" command.
        const interimParsed = parse(trimmedInterim, batterHand, stickyScopeRef.current, currentSpatialRef.current);
        const isAction = interimParsed && ["save", "undo", "cancel"].includes(interimParsed.kind);
        // 2026-05-28 — fast-path for unambiguous chip-group updates.
        // Originally only top-level run/extras flags fast-fired, but
        // the founder reported the same lag on air movement / shot
        // connection / height impact / control. Extended to also
        // fast-fire analytics chip fields (stroke / shot_connection /
        // air_movement / control / keeper_activity / bounce_category /
        // spin / drift / turn / foot / delivery_type) and the
        // height_zone / structured_position spatial fields. The
        // existing firedBallFieldsRef guarantees per-field idempotency
        // across interim → final so fast-firing here can't double-fire
        // when the final lands with the same value. pitch_zone /
        // stump_zone stay on the 400ms debounce because "yorker" /
        // "outside" by themselves are mid-utterance noise that often
        // gets revised by Whisper before the final.
        const FAST_BALL_KEYS = new Set([
          "completed_runs", "is_wide", "is_no_ball", "is_bye", "is_leg_bye",
          "is_dead_ball", "is_free_hit", "is_boundary",
          "is_quick_running", "is_short_run",
          "no_ball_type", "appealed_for",
          // 2026-06-10 — Law 22 wide subtype, paired with the picker
          // shipped in the same batch.
          "wide_type",
        ]);
        const FAST_ANALYTICS_FIELDS = new Set([
          "stroke", "shot_connection", "air_movement", "control",
          "keeper_activity", "bounce_category", "spin_direction",
          "drift_direction", "turn_after_pitch", "foot_movement",
          "delivery_type",
        ]);
        const FAST_SPATIAL_FIELDS = new Set([
          "height_zone", "batter_stump_zone", "structured_position",
        ]);
        const isFastBall = interimParsed && interimParsed.kind === "ball" && (() => {
          const u = interimParsed.updates || {};
          const sp = u.__spatial || {};
          const an = u.__analytics || {};
          const restKeys = Object.keys(u).filter((k) => !k.startsWith("__"));
          const anKeys = Object.keys(an);
          const spKeys = Object.keys(sp);
          // Need at least ONE field actually changing.
          if (!restKeys.length && !anKeys.length && !spKeys.length) return false;
          // Every present key must be in its fast-fire whitelist.
          return (
            restKeys.every((k) => FAST_BALL_KEYS.has(k))
            && anKeys.every((k) => FAST_ANALYTICS_FIELDS.has(k))
            && spKeys.every((k) => FAST_SPATIAL_FIELDS.has(k))
          );
        })();
        if (isAction || isFastBall) {
          if (interimTimerRef.current) clearTimeout(interimTimerRef.current);
          applyParsed(interimParsed, trimmedInterim);
        } else {
          if (interimTimerRef.current) clearTimeout(interimTimerRef.current);
          interimTimerRef.current = setTimeout(() => {
            const parsed = parse(trimmedInterim, batterHand, stickyScopeRef.current, currentSpatialRef.current);
            applyParsed(parsed, trimmedInterim);
            interimTimerRef.current = null;
          }, 400);
        }
      }

      if (!finalText) return;

      // Final landed — cancel any pending interim timer (we've got
      // the authoritative text now).
      if (interimTimerRef.current) {
        clearTimeout(interimTimerRef.current);
        interimTimerRef.current = null;
      }

      const trimmedFinal = finalText.trim();
      const parsed = parse(trimmedFinal, batterHand, stickyScopeRef.current, currentSpatialRef.current);
      // applyParsed will short-circuit if the same text already fired
      // via interim. For "ball" kind with NEW fields the per-field
      // dedup lets the new fields through.
      const handled = applyParsed(parsed, trimmedFinal);

      // ── AI FALLBACK ──────────────────────────────────────────────
      // When the regex parser couldn't extract anything AND the
      // operator has the AI toggle on, send the transcript to Haiku
      // for structured parsing. Stateless POST; only charges 1 ZC
      // when it actually fires (i.e. on misses, not on every ball).
      if (!handled && parsed && parsed.kind === "unrecognized" && aiFallbackEnabled) {
        // Light scope hint — if the operator prefixed a scope word,
        // detectScope already stripped it but the original text still
        // hints at it. Pass it through so Claude focuses the parse.
        const { scope: hintedScope } = detectScope(trimmedFinal.toLowerCase());
        (async () => {
          try {
            const api = (await import("../api/axios")).default;
            const { data } = await api.post("/api/scoring/ai/voice-parse/", {
              transcript: trimmedFinal,
              scope: hintedScope || null,
              bowler_type: bowlerType || null,
            });
            // ── AUTO-CONFIRM (2s) ────────────────────────────────────
            // Don't apply AI results immediately — Claude can misparse
            // a slurred phrase and we don't want the bad data committed
            // before the operator can veto. Queue the apply with a
            // 2-second visible "🤖 Apply: X? Cancel" toast. If the
            // operator does nothing, auto-commits. If they tap Cancel,
            // the apply is dropped and the regex 'unknown' toast they
            // already saw stays accurate. (Founder ask 2026-05-25.)
            if (!data || data.kind === "unrecognized") return;
            // ── Confidence branching (mirrors the PTT path above) ──
            // 2026-05-28 auto-apply threshold: > 0.74 (was 0.9).
            // 2026-06-10 voice audit — same threshold tweaks as the
            // PTT path above: ignore <0.55, default missing → 0.85.
            const conf2 = typeof data.confidence === "number" ? data.confidence : 0.85;
            if (conf2 < 0.55) {
              setLastApplied({ ts: Date.now(), label: `🤖 low confidence (${conf2.toFixed(2)}) — ignored` });
              return;
            }
            if (conf2 > 0.74) {
              applyParsed(data, trimmedFinal);
              setLastApplied({ ts: Date.now(), label: `🤖 ${data.label || "AI parsed"} (${conf2.toFixed(2)})` });
              return;
            }
            // If a prior AI suggestion is still pending, auto-commit
            // it first (operator just gave us a new utterance — they
            // moved on).
            if (pendingTimerRef.current) {
              clearTimeout(pendingTimerRef.current);
              const prev = pendingApply;
              if (prev) applyParsed(prev.parsed, prev.sourceText);
              pendingTimerRef.current = null;
            }
            // 5 sec gives the operator time to read the suggestion +
            // tap Apply/Cancel deliberately. Was 2s; founder report
            // 2026-05-26 ("time for the suggestion should be extended").
            const COUNTDOWN_MS = 5000;
            const expiresAt = Date.now() + COUNTDOWN_MS;
            setPendingApply({
              parsed: data,
              sourceText: trimmedFinal,
              label: data.label || "AI parsed",
              expiresAt,
              confidence: conf2,
            });
            pendingTimerRef.current = setTimeout(() => {
              applyParsed(data, trimmedFinal);
              setPendingApply(null);
              pendingTimerRef.current = null;
            }, COUNTDOWN_MS);
          } catch (e) {
            // Quiet on AI fallback failures (network / 503 / wallet
            // insufficient) — the operator already saw an "unknown"
            // toast from the regex parser, and we don't want to spam.
            // Toggle stays on; next utterance retries.
            // eslint-disable-next-line no-console
            console.warn("voice AI fallback failed:", e?.response?.data || e);
          }
        })();
      }

      // Utterance is complete — reset per-utterance state so the next
      // phrase starts with a clean slate.
      lastAppliedTextRef.current = "";
      firedBallFieldsRef.current = new Set();
    };
    r.onerror = (e) => {
      const msg = e.error || "speech error";
      if (msg === "no-speech") return; // benign
      setError(msg);
      setListening(false);
    };
    r.onend = () => {
      // Auto-restart if still enabled
      if (enabled && listening) {
        try { r.start(); } catch {}
      } else {
        setListening(false);
      }
    };
  }, [enabled, listening]);

  const start = useCallback(() => {
    const r = recRef.current;
    if (!r || !supported) return;
    setError("");
    try {
      r.start();
      setListening(true);
    } catch (e) {
      setError(e.message || "failed to start mic");
    }
  }, [supported]);

  const stop = useCallback(() => {
    const r = recRef.current;
    if (!r) return;
    try { r.stop(); } catch {}
    setListening(false);
  }, []);

  const toggle = useCallback(() => {
    if (listening) stop(); else start();
  }, [listening, start, stop]);

  // Auto-confirm controls — consumer toast can show Apply/Cancel.
  // Now routes through applyParsedRef (exposed from the onresult
  // effect) so the early-commit path is byte-for-byte identical to
  // the 2-second auto-fire. Previous mini-applier missed wicket
  // dismissal/run-out side-effects + the structuredRegion sync,
  // which made Apply appear to do nothing — founder report
  // 2026-05-26 ("on clicking apply there's no selection").
  const confirmPending = useCallback(() => {
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    const p = pendingApply;
    setPendingApply(null);
    if (!p) return;
    const apply = applyParsedRef.current;
    if (apply) {
      apply(p.parsed, p.sourceText);
    } else if (p.parsed?.kind === "ball" && p.parsed.updates) {
      // Defensive fallback — if the ref isn't yet populated (race
      // during initial mount) do the inline mini-applier so the
      // operator's click isn't lost.
      const { __spatial, __analytics, ...rest } = p.parsed.updates;
      setBall?.((prev) => ({
        ...prev,
        ...rest,
        spatial:   { ...(prev.spatial   || {}), ...(__spatial   || {}) },
        analytics: { ...(prev.analytics || {}), ...(__analytics || {}) },
      }));
      setLastApplied({ ts: Date.now(), label: p.label });
    }
  }, [pendingApply, setBall]);

  const cancelPending = useCallback(() => {
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    setPendingApply(null);
    setLastApplied({ ts: Date.now(), label: "✕ AI suggestion cancelled" });
  }, []);

  // PTT entry point — called by VoiceScoringPanel's Whisper button
  // with a transcript from Groq. Dispatches through the same parse +
  // apply chain the browser-STT path uses; AI fallback piggybacks too.
  const applyTranscript = useCallback((text) => {
    applyTranscriptRef.current?.(text);
  }, []);

  return {
    supported, listening, transcript, lastApplied, recentApplied, error,
    start, stop, toggle,
    pendingApply, confirmPending, cancelPending,
    applyTranscript,
  };
}
